import React, { useState, useRef, useEffect, useCallback, useMemo, Component } from "react";
import { useVersionCheck } from "./hooks/useVersionCheck";
import "./App.css";
import "./Premium.css";
import "./PremiumProfile.css";
import { PremiumRegister, PremiumLogin, DailyCounter, PremiumEditProfile } from "./Premium";
import { PremiumProfileForm, generatePersonalizedTip } from "./PremiumProfile";
import CheckinRefeicao from "./CheckinRefeicao";
import "./CheckinRefeicao.css";
import { I18nProvider, useI18n, LanguageSelector } from "./I18nContext";
import NutritionFeed from "./NutritionFeed";
import "./NutritionFeed.css";
import WeeklyReport from "./WeeklyReport";
import "./WeeklyReport.css";
import NotificationPanel from "./NotificationPanel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Timeout para requisições (evita travamentos)
const REQUEST_TIMEOUT = 15000; // 15 segundos
let _ROOT_SCAN_COUNTER = 0; // contador global de scans para diagnóstico

// ═══════════════════════════════════════════════════════════════
// GEOLOCALIZAÇÃO - Detecção Cibi Sana vs Externo
// ═══════════════════════════════════════════════════════════════
const CIBI_SANA_LAT = -23.0373642;
const CIBI_SANA_LNG = -46.9767934;
const CIBI_SANA_RADIUS_METERS = 100; // Raio de detecção (metros)

function haversineDistance(lat1, lon1, lat2, lon2) {
  const R = 6371000; // Raio da Terra em metros
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// Error Boundary para capturar erros da câmera
class CameraErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[CameraErrorBoundary] Erro capturado:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="camera-error-fallback" style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          padding: '20px',
          textAlign: 'center',
          backgroundColor: '#1a1a2e',
          color: '#fff'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📷</div>
          <h3 style={{ marginBottom: '12px' }}>Erro na Câmera</h3>
          <p style={{ marginBottom: '16px', color: '#aaa' }}>
            Ocorreu um problema com a câmera. Por favor, recarregue a página.
          </p>
          <button 
            onClick={() => window.location.reload()}
            style={{
              padding: '12px 24px',
              backgroundColor: '#4CAF50',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            Recarregar Página
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Componente Welcome Popup - Boas-vindas com seleção de idioma
function WelcomePopup({ onClose }) {
  const { languages, changeLanguage, currentLang, t } = useI18n();
  
  const handleSelectLanguage = (langCode) => {
    changeLanguage(langCode);
    onClose();
  };
  
  return (
    <div className="welcome-overlay" data-testid="welcome-popup">
      <div className="welcome-popup">
        <div className="welcome-header">
          <img src="/images/soulnutri-logo.png" alt="SoulNutri" className="welcome-logo" />
          <h1>SoulNutri<span className="tm">®</span></h1>
        </div>
        
        <p className="welcome-tagline">Porque nutre também a sua alma</p>
        
        <div className="welcome-message">
          <p>🌍 Escolha seu idioma / Choose your language:</p>
        </div>
        
        <div className="welcome-languages">
          {languages.map(lang => (
            <button
              key={lang.code}
              className={`welcome-lang-btn ${lang.code === currentLang ? 'active' : ''}`}
              onClick={() => handleSelectLanguage(lang.code)}
              data-testid={`welcome-lang-${lang.code}`}
            >
              <span className="welcome-flag">{lang.flag}</span>
              <span className="welcome-lang-name">{lang.native}</span>
            </button>
          ))}
        </div>
        
        <div className="welcome-features">
          <div className="feature">📸 Aponte • Identifique • Saiba</div>
          <div className="feature">🥗 499+ pratos cadastrados</div>
          <div className="feature">⚡ Rápido e preciso</div>
        </div>
        
        <div className="welcome-location-info" style={{
          marginTop: '12px',
          padding: '10px',
          backgroundColor: 'rgba(255,255,255,0.1)',
          borderRadius: '8px',
          fontSize: '12px',
          color: '#ccc',
          textAlign: 'center'
        }}>
          📷 Aponte a câmera para o prato e o SoulNutri identifica automaticamente
        </div>
        
        <button className="welcome-start-btn" onClick={onClose} data-testid="welcome-start">
          {t('tap_to_photo', 'Começar')} →
        </button>
      </div>
    </div>
  );
}

// Componente Tutorial do Scanner Contínuo
function ScannerTutorial({ onClose }) {
  const [step, setStep] = useState(1);
  const { t } = useI18n();
  
  const steps = [
    {
      icon: "📸",
      title: t('tutorial_step1_title', 'Aponte e Fotografe'),
      desc: t('tutorial_step1_desc', 'Enquadre o prato na tela e toque para tirar uma foto. Em segundos você terá todas as informações.'),
      visual: "scan-animation"
    },
    {
      icon: "✨",
      title: t('tutorial_step2_title', 'Escolha Consciente'),
      desc: t('tutorial_step2_desc', 'Veja ingredientes, benefícios, alertas de alérgenos e muito mais. Tudo para você fazer a melhor escolha.'),
      visual: "tap-animation"
    },
    {
      icon: "🎯",
      title: t('tutorial_step3_title', 'Seu Prato, Sua Escolha'),
      desc: t('tutorial_step3_desc', 'Monte seu prato ideal com informações completas. Saiba exatamente o que está comendo!'),
      visual: "plate-animation"
    }
  ];
  
  const currentStep = steps[step - 1];
  
  const handleNext = () => {
    if (step < 3) {
      setStep(step + 1);
    } else {
      localStorage.setItem('soulnutri_tutorial_seen', 'true');
      onClose();
    }
  };
  
  const handleSkip = () => {
    localStorage.setItem('soulnutri_tutorial_seen', 'true');
    onClose();
  };
  
  return (
    <div className="tutorial-overlay" data-testid="scanner-tutorial">
      <div className="tutorial-popup">
        <button className="tutorial-skip" onClick={handleSkip} data-testid="tutorial-skip">
          {t('skip', 'Pular')} →
        </button>
        
        <div className="tutorial-progress">
          {[1, 2, 3].map(s => (
            <div key={s} className={`progress-dot ${s === step ? 'active' : ''} ${s < step ? 'done' : ''}`} />
          ))}
        </div>
        
        <div className={`tutorial-visual ${currentStep.visual}`}>
          <span className="tutorial-icon">{currentStep.icon}</span>
          {currentStep.visual === 'scan-animation' && (
            <div className="scan-waves">
              <div className="wave"></div>
              <div className="wave"></div>
              <div className="wave"></div>
            </div>
          )}
          {currentStep.visual === 'tap-animation' && (
            <div className="tap-finger">👆</div>
          )}
          {currentStep.visual === 'plate-animation' && (
            <div className="plate-items-anim">
              <span>🍗</span><span>🥗</span><span>🍚</span>
            </div>
          )}
        </div>
        
        <h2 className="tutorial-title">{currentStep.title}</h2>
        <p className="tutorial-desc">{currentStep.desc}</p>
        
        <div className="tutorial-actions">
          {step > 1 && (
            <button className="tutorial-back" onClick={() => setStep(step - 1)}>
              ← {t('back', 'Voltar')}
            </button>
          )}
          <button className="tutorial-next" onClick={handleNext} data-testid="tutorial-next">
            {step < 3 ? t('next', 'Próximo') : t('start_using', 'Começar a usar')} →
          </button>
        </div>
      </div>
    </div>
  );
}

function App() {
  const { t, currentLang } = useI18n();
  const { hasUpdate, serverVersion, triggerHardUpdate } = useVersionCheck();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [stream, setStream] = useState(null);
  const streamRef = useRef(null); // Ref para acessar stream no cleanup
  const [error, setError] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [dishes, setDishes] = useState([]);
  const [feedbackSent, setFeedbackSent] = useState(false);
  const [showCorrectionFlow, setShowCorrectionFlow] = useState(false);
  const [correctionSearch, setCorrectionSearch] = useState('');
  const [lastImageBlob, setLastImageBlob] = useState(null);
  const [newDishName, setNewDishName] = useState("");
  const [creatingDish, setCreatingDish] = useState(false);
  const [searchFilter, setSearchFilter] = useState("");
  const [multiMode, setMultiMode] = useState(false);
  const [multiResult, setMultiResult] = useState(null);
  const [cameraError, setCameraError] = useState(null);
  const [previewImageUrl, setPreviewImageUrl] = useState(null); // Preview da imagem selecionada
  const [showMultiCorrection, setShowMultiCorrection] = useState(false); // Modal correção multi
  const [multiCorrections, setMultiCorrections] = useState({ principal: '', acompanhamentos: '' }); // Correções
  // Fluxo Único Inteligente - acumula itens do prato
  const [plateItems, setPlateItems] = useState(() => {
    try {
      const saved = localStorage.getItem('soulnutri_plate_items');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [showAddMore, setShowAddMore] = useState(false); // Modal "Adicionar mais?"
  const [showFirstTimeHelp, setShowFirstTimeHelp] = useState(false); // Popup explicativo primeira vez
  const [viewMode, setViewMode] = useState(() => {
    try {
      const savedPlate = localStorage.getItem('soulnutri_plate_items');
      const items = savedPlate ? JSON.parse(savedPlate) : [];
      if (items.length > 0) {
        const savedMode = localStorage.getItem('soulnutri_view_mode');
        return savedMode || 'buffet';
      }
      return 'buffet';
    } catch { return 'buffet'; }
  });
  // Persistir plateItems e viewMode no localStorage
  useEffect(() => {
    localStorage.setItem('soulnutri_plate_items', JSON.stringify(plateItems));
  }, [plateItems]);
  useEffect(() => {
    localStorage.setItem('soulnutri_view_mode', viewMode);
  }, [viewMode]);
  // ───────────────────────────────────────────────────────────
  // Warm-up silencioso do ONNX (P1)
  // - 1x por sessao do browser (sessionStorage)
  // - nao bloqueia UI, nao exibe erro, timeout curto
  // - falha silenciosa (catch vazio)
  // ───────────────────────────────────────────────────────────
  useEffect(() => {
    try {
      if (sessionStorage.getItem('soulnutri_warmup_done') === '1') return;
      const ctrl = new AbortController();
      const timeoutId = setTimeout(() => ctrl.abort(), 4000);
      fetch(`${API}/ai/warmup`, { method: 'GET', signal: ctrl.signal })
        .then(() => { try { sessionStorage.setItem('soulnutri_warmup_done', '1'); } catch {} })
        .catch(() => { /* silencioso */ })
        .finally(() => clearTimeout(timeoutId));
    } catch { /* silencioso */ }
  }, []);
  // IA sob demanda
  const [loadingIA, setLoadingIA] = useState(false); // Carregando IA
  // Galeria de fotos capturadas
  const [photoGallery, setPhotoGallery] = useState(() => {
    const saved = localStorage.getItem('soulnutri_gallery');
    return saved ? JSON.parse(saved) : [];
  });
  const [showGalleryView, setShowGalleryView] = useState(false); // Modal galeria
  // Modo Scanner Contínuo para Buffet (DESATIVADO - usando modo foto simples)
  const [scannerMode, setScannerMode] = useState(false); // Desativado por padrão
  const [lastScanTime, setLastScanTime] = useState(0);
  const [scannerResult, setScannerResult] = useState(null); // Resultado do scanner (overlay)
  const scanIntervalRef = useRef(null);
  // Premium states
  const [showPremium, setShowPremium] = useState(null); // null, 'login', 'register', 'dashboard', 'profile'
  const [premiumBlockedMsg, setPremiumBlockedMsg] = useState('');
  
  // Radar de Notícias - Fatos sobre alimentos
  const [radarInfo, setRadarInfo] = useState(null); // {has_alert, message, facts}
  const [showRadarDetails, setShowRadarDetails] = useState(false); // Modal com fatos detalhados

  // Radar Alimentar - Breaking News Contextual (Camada 1, Premium only)
  // Origem: result.contextual_breaking_news vindo de /api/ai/identify
  const [breakingNewsModal, setBreakingNewsModal] = useState(null); // item completo ou null
  
  // TTS - Acessibilidade
  const [ttsPlaying, setTtsPlaying] = useState(false);
  const [ttsLoading, setTtsLoading] = useState(false);
  const [ttsError, setTtsError] = useState(null);
  const ttsAudioRef = useRef(null);
  
  // Enrich Premium - Loading state
  const [enrichLoading, setEnrichLoading] = useState(false);
  
  const playDishAudio = async (dishData, premiumTts = false) => {
    if (ttsAudioRef.current) {
      ttsAudioRef.current.pause();
      ttsAudioRef.current = null;
      setTtsPlaying(false);
      return;
    }
    
    setTtsLoading(true);
    setTtsError(null);
    try {
      const res = await fetch(`${API}/ai/tts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dish_data: dishData, premium_tts: premiumTts })
      });
      
      let contentType = '';
try {
  contentType = res.headers.get('content-type') || '';
} catch {}

if (!res.ok || !contentType.includes('audio')) {
  let errorText = `HTTP ${res.status}`;
  try {
    errorText = await res.clone().text();
  } catch {}

  throw new Error(`Erro do servidor: ${res.status} - ${errorText.substring(0, 100)}`);
 }
      
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      ttsAudioRef.current = audio;
      
      audio.onended = () => {
        setTtsPlaying(false);
        ttsAudioRef.current = null;
        URL.revokeObjectURL(url);
      };
      audio.onerror = () => {
        setTtsError('Erro ao reproduzir');
        setTtsPlaying(false);
        ttsAudioRef.current = null;
        setTimeout(() => setTtsError(null), 4000);
      };
      
      setTtsPlaying(true);
      await audio.play();
    } catch (e) {
      console.error('[TTS] Erro:', e);
      setTtsError('Audio indisponivel');
      setTtsPlaying(false);
      setTimeout(() => setTtsError(null), 4000);
    } finally {
      setTtsLoading(false);
    }
  };
  
  const [premiumUser, setPremiumUser] = useState(null);
  const isAdminUser = premiumUser?.is_admin === true;
  const premiumUserRef = useRef(null);
  const sessionRetryRef = useRef(null); // handle de retry controlado de sessão
  // Ref para rastrear pratos já enriquecidos (evita mutação direta de estado)
  const enrichedDishesRef = useRef(new Set());
  // Sync ref com state para evitar stale closure em callbacks memoizados
  useEffect(() => { premiumUserRef.current = premiumUser; }, [premiumUser]);
  // 🔒 Normalizador de texto seguro (EVITA CRASH .toLowerCase)
const safeText = (v) => {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v.toLowerCase();
  if (typeof v === 'number') return String(v).toLowerCase();
  if (Array.isArray(v)) return v.map(safeText).join(' ');
  if (typeof v === 'object') {
    return safeText(v.text || v.label || JSON.stringify(v));
  }
  return '';
};
// 🔒 Render-safe para JSX: garante que NUNCA renderizamos um objeto direto (React error #31)
// Aceita string/number normalmente; se for objeto, extrai 'texto'/'text'/'titulo'/etc.
const renderTextSafe = (v) => {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string' || typeof v === 'number') return String(v);
  if (Array.isArray(v)) return v.map(renderTextSafe).filter(Boolean).join(', ');
  if (typeof v === 'object') {
    const extracted = v.texto || v.text || v.titulo || v.title || v.mensagem || v.message || v.descricao || v.description || '';
    return renderTextSafe(extracted);
  }
  return '';
};
  const [dailySummary, setDailySummary] = useState(null);
  const [showCheckin, setShowCheckin] = useState(false); // Check-in de refeição
  const [personalizedTips, setPersonalizedTips] = useState(null); // Dicas personalizadas
  // Menu e PWA
  const [showMenu, setShowMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifUnread, setNotifUnread] = useState(0);
  const [mealRegistered, setMealRegistered] = useState(false); // Confirmação de registro
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installBannerDismissed, setInstallBannerDismissed] = useState(
    () => !!localStorage.getItem('soulnutri_install_dismissed')
  );
  // Tela de permissões unificada
  const [showPermissions, setShowPermissions] = useState(() => {
    return !localStorage.getItem('soulnutri_permissions_granted');
  });
  const [permissionsStatus, setPermissionsStatus] = useState({
    camera: 'pending',
    location: 'pending'
  });
  // Detecção de localização (Cibi Sana vs Externo)
  const [detectedRestaurant, setDetectedRestaurant] = useState(null);
  const [showLocationPrompt, setShowLocationPrompt] = useState(false);
  const isCibiSana = detectedRestaurant === 'cibi_sana';
  const canShowAdminTools = isAdminUser && isCibiSana;
  const userAgent = navigator.userAgent || '';
  const isIOS = /iPad|iPhone|iPod/.test(userAgent);
  const isSafari = /^((?!chrome|android).)*safari/i.test(userAgent);
  const isAndroid = /Android/i.test(userAgent);
  const isStandalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true;
  const [isInstalled, setIsInstalled] = useState(false);
  const shouldShowInstallBanner = !isInstalled && !isStandalone && !installBannerDismissed;
  const shouldShowIOSInstallHelp = shouldShowInstallBanner && isIOS && isSafari;
  const shouldShowAndroidInstallHelp = shouldShowInstallBanner && isAndroid;

  // Welcome popup com seleção de idioma
  const [showWelcome, setShowWelcome] = useState(() => {
    return !localStorage.getItem('soulnutri_welcomed');
  });
  // Tutorial do Scanner Contínuo
  const [showScannerTutorial, setShowScannerTutorial] = useState(false);
  const [showNutriFeed, setShowNutriFeed] = useState(false);
  const [showWeeklyReport, setShowWeeklyReport] = useState(false);
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const cameraReadyAtRef = useRef(0);
  const fileInputRef = useRef(null);
  const loadingRef = useRef(false);
  const abortControllerRef = useRef(null);
  const premiumCycleBusyRef = useRef(false);
  const mountedRef = useRef(true);
  const lastTouchRef = useRef(0);

  // Função para solicitar todas as permissões de uma vez
  const requestAllPermissions = async () => {
    let cameraGranted = false;
    
    // 1. Solicitar permissão de câmera
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      stream.getTracks().forEach(t => t.stop());
      cameraGranted = true;
      setPermissionsStatus(prev => ({ ...prev, camera: 'granted' }));
    } catch (e) {
      setPermissionsStatus(prev => ({ ...prev, camera: 'denied' }));
    }
    
    // 2. Solicitar permissão de localização (detectar Cibi Sana)
    detectLocation();
    
    // Marcar permissões como solicitadas
    localStorage.setItem('soulnutri_permissions_granted', 'true');
    setShowPermissions(false);
    
    // Iniciar câmera se permitido
    if (cameraGranted) {
      startCamera();
    }
  };

  // Detectar localização GPS para determinar Cibi Sana vs Externo
  const watchIdRef = useRef(null);
  
  const detectLocation = () => {
    if (!navigator.geolocation) {
      setPermissionsStatus(prev => ({ ...prev, location: 'denied' }));
      if (!localStorage.getItem('soulnutri_restaurant')) setShowLocationPrompt(true);
      return;
    }
    
    setPermissionsStatus(prev => ({ ...prev, location: 'pending' }));
    
    // Limpar watcher anterior
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
    }
    
    // Fallback: se GPS não responder em 4s, mostrar seleção manual
    // Usa localStorage (síncrono) para evitar stale closures do React state
    const fallbackTimer = setTimeout(() => {
      if (!localStorage.getItem('soulnutri_restaurant')) {
        console.log('[GPS] Timeout 4s - mostrando seleção manual');
        setShowLocationPrompt(true);
      }
    }, 4000);
    
    // Monitoramento contínuo de localização
    watchIdRef.current = navigator.geolocation.watchPosition(
      (position) => {
        clearTimeout(fallbackTimer);
        
        const dist = haversineDistance(
          position.coords.latitude, position.coords.longitude,
          CIBI_SANA_LAT, CIBI_SANA_LNG
        );
        const accuracy = position.coords.accuracy || 0;
        const effectiveRadius = CIBI_SANA_RADIUS_METERS + Math.min(accuracy, 50);
        const isInsideCibiSanaZone = dist <= effectiveRadius;
        
        const newRestaurant = isInsideCibiSanaZone ? 'cibi_sana' : 'external';
        
        const isManual = localStorage.getItem('soulnutri_location_manual') === 'true';
        
        if (isManual) {
          // Se GPS confirma Cibi Sana, força correção e remove trava
          if (newRestaurant === 'cibi_sana') {
            localStorage.removeItem('soulnutri_location_manual');
            setDetectedRestaurant('cibi_sana');
            localStorage.setItem('soulnutri_restaurant', 'cibi_sana');
          } else {
            // GPS confirma saída da zona → respeitar GPS, remover trava manual
            localStorage.removeItem('soulnutri_location_manual');
            setDetectedRestaurant('external');
            localStorage.setItem('soulnutri_restaurant', 'external');
          }
          return;
        }
        
        setDetectedRestaurant(prev => {
          if (prev && prev !== newRestaurant) {
            console.log(`[GPS] Local mudou: ${prev} → ${newRestaurant}. Nova refeição.`);
            setPlateItems([]);
            setResult(null);
            setViewMode('camera');
          }
          return newRestaurant;
        });
        
        localStorage.setItem('soulnutri_restaurant', newRestaurant);
        setPermissionsStatus(prev => ({ ...prev, location: 'granted' }));
        setShowLocationPrompt(false);
        console.log(`[GPS] Dist: ${Math.round(dist)}m | Precisão: ${Math.round(accuracy)}m | Modo: ${newRestaurant}`);
      },
      (error) => {
        clearTimeout(fallbackTimer);
        console.warn('[GPS] Erro:', error.message);
        setPermissionsStatus(prev => ({ ...prev, location: 'denied' }));
        if (!localStorage.getItem('soulnutri_restaurant')) {
          setShowLocationPrompt(true);
        }
      },
      { enableHighAccuracy: true, timeout: 5000, maximumAge: 30000 }
    );
  };

  // Selecao manual de localização
  const selectLocationManual = (location) => {
    // Parar GPS watcher para evitar conflito
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    setDetectedRestaurant(location);
    localStorage.setItem('soulnutri_restaurant', location);
    localStorage.setItem('soulnutri_location_manual', 'true');
    setShowLocationPrompt(false);
  };

  // Valor do restaurant para enviar na API
  // Prioridade: detectedRestaurant (GPS/manual) > seleção manual explícita > external
  const getRestaurantValue = () => {
    // P1: detectedRestaurant confirmado por GPS ou selectLocationManual()
    if (detectedRestaurant === 'cibi_sana') return 'cibi_sana';
    if (detectedRestaurant === 'external') return 'external';
    // P2: detectedRestaurant=null (GPS pendente/negado, nunca disparou)
    // Consultar SOMENTE seleção manual explícita com flag persistente
    const isManual = localStorage.getItem('soulnutri_location_manual') === 'true';
    if (isManual && localStorage.getItem('soulnutri_restaurant') === 'cibi_sana') return 'cibi_sana';
    // Sem confirmação de localização → external
    return 'external';
  };

  useEffect(() => { 
    mountedRef.current = true;
    checkStatus(); 
    
    // Só inicia câmera automaticamente se as permissões já foram concedidas
    if (!showPermissions) {
      startCamera();
      // Detectar localização ao iniciar (re-verifica a cada sessão)
      detectLocation();
    }
    
    loadDishes();
    checkPremiumSession();
    
    // Detectar se já está instalado como PWA
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }
    
       // Capturar evento de instalação PWA
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    
    // Gerenciar câmera quando o app perde/ganha foco (previne travamentos)
    let cameraWasActive = false;
    const handleVisibility = () => {
      if (document.hidden) {
        // Salvar estado antes de parar
        cameraWasActive = !!streamRef.current;
        if (cameraWasActive) {
          console.log('[Camera] App em background, pausando...');
          stopCameraInternal();
        }
      } else {
        if (cameraWasActive) {
          // No mobile, getUserMedia exige gesto do usuário.
          // Não reiniciar automaticamente ao voltar do background.
          console.log('[Camera] App em foreground; aguardando ação do usuário para retomar.');
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    
    return () => {
      mountedRef.current = false;
      stopCameraInternal();
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      document.removeEventListener('visibilitychange', handleVisibility);
      // Cancelar requisições pendentes
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      // Cancelar retry de sessão pendente
      if (sessionRetryRef.current) {
        clearTimeout(sessionRetryRef.current);
      }
      // Limpar monitoramento GPS
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
    };
  }, []);

  // Camera restart é feito pelos botões diretamente (getUserMedia requer gesto do usuário no mobile)


  // Detectar localização quando tela de permissões é fechada
  useEffect(() => {
    if (!showPermissions && !detectedRestaurant) {
      detectLocation();
    }
  }, [showPermissions]); // eslint-disable-line react-hooks/exhaustive-deps

  // ═══════════════════════════════════════════════════════════════
    // ═══════════════════════════════════════════════════════════════
  // ENRICH PREMIUM - useEffect dedicado (ZERO stale closure)
  // Dispara quando um novo resultado é identificado E o user é premium
  // ═══════════════════════════════════════════════════════════════
  useEffect(() => {
    if (!result?.ok || !result?.identified || !premiumUser) { setEnrichLoading(false); return; }

    const dishName = result.dish_display;
    if (!dishName) return;

    // Evitar chamadas duplicadas para o mesmo prato (via ref — sem mutação de estado)
    if (enrichedDishesRef.current.has(dishName)) return;
    enrichedDishesRef.current.add(dishName);

    let cancelled = false;
    const currentDishName = dishName;
    const currentUserPin = premiumUser.pin;
    const currentUserName = premiumUser.nome;

    setEnrichLoading(true);
    console.log(`[FINAL_PROOF] scan_id=${_ROOT_SCAN_COUNTER} ENRICH_START dish=${currentDishName}`);
    console.log('[ENRICH] Iniciando para:', currentDishName, '| Premium:', currentUserName);

    fetch(`${API}/ai/enrich`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        nome: currentDishName,
        ingredientes: result.ingredientes || [],
        pin: currentUserPin,
        user_nome: currentUserName
      })
    })
      .then((r) => r.json())
      .then((enrichData) => {
        if (cancelled) return;

        if (!enrichData.ok) {
          console.warn('[ENRICH] Resposta não ok:', enrichData);
          setEnrichLoading(false);
          return;
        }

        console.log('[PREMIUM_DEBUG] dish:', currentDishName);
        console.log('[PREMIUM_DEBUG] enrich raw payload:', enrichData);

        console.log('[PREMIUM_DEBUG] field-types:', {
          descricao: typeof enrichData.descricao,
          description: typeof enrichData.description,

          ingredientes: Array.isArray(enrichData.ingredientes)
            ? 'array'
            : typeof enrichData.ingredientes,
          ingredients: Array.isArray(enrichData.ingredients)
            ? 'array'
            : typeof enrichData.ingredients,

          beneficios: Array.isArray(enrichData.beneficios)
            ? 'array'
            : typeof enrichData.beneficios,
          benefits: Array.isArray(enrichData.benefits)
            ? 'array'
            : typeof enrichData.benefits,

          riscos: Array.isArray(enrichData.riscos)
            ? 'array'
            : typeof enrichData.riscos,
          risks: Array.isArray(enrichData.risks)
            ? 'array'
            : typeof enrichData.risks,

          categoria: typeof enrichData.categoria,
          category: typeof enrichData.category,
        });

        console.log('[PREMIUM_DEBUG] first-items:', {
          ingredientes_0: Array.isArray(enrichData.ingredientes) ? enrichData.ingredientes[0] : null,
          ingredients_0: Array.isArray(enrichData.ingredients) ? enrichData.ingredients[0] : null,
          beneficios_0: Array.isArray(enrichData.beneficios) ? enrichData.beneficios[0] : null,
          benefits_0: Array.isArray(enrichData.benefits) ? enrichData.benefits[0] : null,
          riscos_0: Array.isArray(enrichData.riscos) ? enrichData.riscos[0] : null,
          risks_0: Array.isArray(enrichData.risks) ? enrichData.risks[0] : null,
        });

        console.log('[PREMIUM_DEBUG] object-fields:', {
          descricao_is_object:
            enrichData.descricao !== null &&
            typeof enrichData.descricao === 'object',
          description_is_object:
            enrichData.description !== null &&
            typeof enrichData.description === 'object',

          ingredientes_has_object:
            Array.isArray(enrichData.ingredientes) &&
            enrichData.ingredientes.some((v) => v && typeof v === 'object'),
          ingredients_has_object:
            Array.isArray(enrichData.ingredients) &&
            enrichData.ingredients.some((v) => v && typeof v === 'object'),

          beneficios_has_object:
            Array.isArray(enrichData.beneficios) &&
            enrichData.beneficios.some((v) => v && typeof v === 'object'),
          benefits_has_object:
            Array.isArray(enrichData.benefits) &&
            enrichData.benefits.some((v) => v && typeof v === 'object'),

          riscos_has_object:
            Array.isArray(enrichData.riscos) &&
            enrichData.riscos.some((v) => v && typeof v === 'object'),
          risks_has_object:
            Array.isArray(enrichData.risks) &&
            enrichData.risks.some((v) => v && typeof v === 'object'),

          categoria_is_object:
            enrichData.categoria !== null &&
            typeof enrichData.categoria === 'object',
          category_is_object:
            enrichData.category !== null &&
            typeof enrichData.category === 'object',
        });

        const enrichFields = {
          beneficios: enrichData.beneficios || [],
          riscos: enrichData.riscos || [],
          curiosidade: enrichData.curiosidade || '',
          combinacoes: enrichData.combinacoes || [],
          noticias: enrichData.noticias || [],
          alertas_historico: enrichData.alertas_historico || [],
          mito_verdade: enrichData.mito_verdade || null
        };

        const enrichNutrition =
          enrichData.nutrition && Object.keys(enrichData.nutrition).length > 0
            ? enrichData.nutrition
            : null;

        // 1) Atualizar o result (se ainda estiver na tela)
        setResult((prev) => {
          if (!prev || prev.dish_display !== currentDishName) return prev;

          const updated = {
            ...prev,
            beneficios:
              enrichFields.beneficios.length > 0 ? enrichFields.beneficios : prev.beneficios,
            riscos:
              enrichFields.riscos.length > 0 ? enrichFields.riscos : prev.riscos,
            curiosidade: enrichFields.curiosidade || prev.curiosidade,
            combinacoes:
              enrichFields.combinacoes.length > 0 ? enrichFields.combinacoes : prev.combinacoes,
            noticias:
              enrichFields.noticias.length > 0 ? enrichFields.noticias : prev.noticias,
            mito_verdade: enrichFields.mito_verdade || prev.mito_verdade
          };

          if (enrichFields.alertas_historico.length > 0 && prev.premium) {
            updated.premium = {
              ...prev.premium,
              alertas_historico: enrichFields.alertas_historico
            };
          }

          if (enrichNutrition) {
            updated.nutrition = { ...(prev.nutrition || {}), ...enrichNutrition };
          }

          return updated;
        });

        // 2) Atualizar plateItems (caso o usuário já tenha adicionado ao prato)
        setPlateItems((prev) =>
          prev.map((item) => {
            if (item.dish_display !== currentDishName) return item;

            return {
              ...item,
              beneficios:
                enrichFields.beneficios.length > 0 ? enrichFields.beneficios : item.beneficios,
              riscos:
                enrichFields.riscos.length > 0 ? enrichFields.riscos : item.riscos,
              curiosidade: enrichFields.curiosidade || item.curiosidade,
              combinacoes:
                enrichFields.combinacoes.length > 0 ? enrichFields.combinacoes : item.combinacoes,
              noticias:
                enrichFields.noticias.length > 0 ? enrichFields.noticias : item.noticias,
              mito_verdade: enrichFields.mito_verdade || item.mito_verdade,
              ...(enrichNutrition
                ? { nutrition: { ...(item.nutrition || {}), ...enrichNutrition } }
                : {})
            };
          })
        );

        console.log('[ENRICH] Premium data recebido para:', currentDishName);
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn('[ENRICH] Erro:', err);
      })
      .finally(() => {
        if (!cancelled) setEnrichLoading(false);
        if (premiumUser) {
          premiumCycleBusyRef.current = false;
        }
      });

    return () => {
      cancelled = true;
    };
    }, [result?.ok, result?.identified, result?.dish_display, premiumUser]); // eslint-disable-line react-hooks/exhaustive-deps

  // ──────────────────────────────────────────────────────────────────────
  // Telemetria simétrica do Radar Alimentar (Camada 1, Breaking News).
  // Casa com o log backend `[BREAKING_NEWS] render` em /api/ai/identify.
  // Sem dados sensíveis: somente titulo (truncado), polaridade, origem, tier.
  // ──────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const bn = result?.contextual_breaking_news;
    if (!bn) return;
    const titulo = (bn.titulo || '').slice(0, 80);
    // eslint-disable-next-line no-console
    console.log(
      `[RADAR_ALIMENTAR] rendered dish=${result?.dish || ''} ` +
      `polaridade=${bn.polaridade || 'neutro'} ` +
      `origem=${bn.origem || ''} tier=${bn.tier || ''} ` +
      `titulo=${JSON.stringify(titulo)}`
    );
  }, [result?.contextual_breaking_news, result?.dish]);

  const checkStatus = async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      const res = await fetch(`${API}/ai/status`, { signal: controller.signal });
      clearTimeout(timeoutId);
      if (mountedRef.current) setStatus(await res.json());
    } catch {
      if (mountedRef.current) setStatus({ ok: false });
    }
  };

  const loadDishes = async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      const res = await fetch(`${API}/ai/dishes`, { signal: controller.signal });
      clearTimeout(timeoutId);
      const data = await res.json();
      if (data.ok && mountedRef.current) setDishes(data.dishes || []);
    } catch (e) {
      console.error('Erro ao carregar pratos:', e);
    }
  };
  // Verificar sessão Premium salva
  // ─────────────────────────────────────────────────────────────
  // PREMIUM SESSION GUARD
  // Regra: SOMENTE "Nome ou PIN incorreto" limpa sessão.
  // Tudo mais (500, 502, timeout, rede, AbortError) → mantém usuário logado.
  // ─────────────────────────────────────────────────────────────
  const checkPremiumSession = async (retryCount = 0) => {
    const pin  = localStorage.getItem('soulnutri_pin');
    const nome = localStorage.getItem('soulnutri_nome');

    if (!pin || !nome) return;

    // Restaurar usuário do cache imediatamente — não bloqueia UI durante verificação
    const cachedRaw = localStorage.getItem('soulnutri_user');
    if (cachedRaw && !premiumUserRef.current) {
      try {
        const cachedUser = JSON.parse(cachedRaw);
        if (mountedRef.current) {
          setPremiumUser({ ...cachedUser, pin });
          console.log('[PREMIUM_SESSION] cached_user_restored attempt=' + retryCount);
        }
      } catch (_) {}
    }

    try {
      const controller = new AbortController();
      // 10s timeout — cobre cold start do Render (~8s típico)
      const timeoutId = setTimeout(() => controller.abort(), 10000);
      const fd = new FormData();
      fd.append('pin',  pin);
      fd.append('nome', nome);

      const res = await fetch(`${API}/premium/login`, {
        method: 'POST',
        body: fd,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      let data;
      try { data = await res.json(); }
      catch (_) { throw new Error('invalid_json_response'); }

      if (!mountedRef.current) return;

      if (data.ok) {
        if (data.premium_bloqueado) {
          // Conta bloqueada/expirada — única exceção além de credencial inválida que força logout
          localStorage.removeItem('soulnutri_pin');
          localStorage.removeItem('soulnutri_nome');
          localStorage.removeItem('soulnutri_user');
          setPremiumUser(null);
          setPremiumBlockedMsg(data.message || 'Acesso Premium bloqueado.');
          setShowPremium('login');
          console.log('[PREMIUM_SESSION] bloqueado_clear');
          return;
        }
        // Sucesso: atualizar cache e estado
        localStorage.setItem('soulnutri_user', JSON.stringify(data.user));
        setPremiumUser({ ...data.user, pin });
        console.log('[PREMIUM_SESSION] valid nome=' + data.user?.nome);
        loadDailySummary();
        loadNotifCount(pin);

      } else if (data.error === 'Nome ou PIN incorreto') {
        // ÚNICA situação que confirma credencial inválida → limpar sessão
        localStorage.removeItem('soulnutri_pin');
        localStorage.removeItem('soulnutri_nome');
        localStorage.removeItem('soulnutri_user');
        setPremiumUser(null);
        console.warn('[PREMIUM_SESSION] invalid_credentials_clear error=' + data.error);

      } else {
        // Qualquer outro ok:false (erro de servidor, DB ocupado, etc.) → temporário
        console.warn('[PREMIUM_SESSION] temporary_error_keep_session error=' + (data.error || 'unknown'));
        _scheduleSessionRetry(retryCount);
      }

    } catch (e) {
      if (!mountedRef.current) return;
      // Rede, timeout, AbortError, JSON inválido — temporário, manter sessão
      console.warn('[PREMIUM_SESSION] temporary_error_keep_session network=' + (e.message || e));
      _scheduleSessionRetry(retryCount);
    }
  };

  const _scheduleSessionRetry = (retryCount) => {
    if (retryCount >= 2) return; // máx 2 tentativas (5s e 15s)
    const delay = retryCount === 0 ? 5000 : 15000;
    console.log('[PREMIUM_SESSION] retry_scheduled delay=' + delay + 'ms attempt=' + (retryCount + 1));
    if (sessionRetryRef.current) clearTimeout(sessionRetryRef.current);
    sessionRetryRef.current = setTimeout(() => {
      if (mountedRef.current) checkPremiumSession(retryCount + 1);
    }, delay);
  };

  // Carregar contagem de notificações não lidas
const loadNotifCount = async (pin) => {
  try {
    if (!pin) return;

    const res = await fetch(`${API}/notifications/${pin}`);

    if (!res.ok) {
      let errorText = `HTTP ${res.status}`;
      try { errorText = await res.clone().text(); } catch {}
      console.warn('[NOTIF] Erro ao carregar notificações:', errorText);
      return;
    }

    let data;
    try {
      data = await res.json();
    } catch {
      console.warn('[NOTIF] Falha ao processar resposta do servidor');
      return;
    }

    if (data?.ok) {
      setNotifUnread(data.unread || 0);
    }

  } catch (e) {
    console.warn('[NOTIF] Erro inesperado:', e?.message);
  }
};

  // Carregar resumo diário
  const loadDailySummary = async () => {
    try {
      const pin = localStorage.getItem('soulnutri_pin');
      const nome = localStorage.getItem('soulnutri_nome');
      if (!pin || !nome) return;
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      const fd = new FormData();
      fd.append('pin', pin);
      fd.append('nome', nome);
      const res = await fetch(`${API}/premium/login`, { 
        method: 'POST', 
        body: fd,
        signal: controller.signal 
      });
      clearTimeout(timeoutId);
      const data = await res.json();
      
      if (!mountedRef.current) return;
      
      if (data.ok && data.daily_log) {
        setDailySummary({
          nome: data.user.nome,
          meta: data.user.meta_calorica?.meta_sugerida || 2000,
          consumido: data.daily_log.calorias_total || 0,
          restante: (data.user.meta_calorica?.meta_sugerida || 2000) - (data.daily_log.calorias_total || 0),
          percentual: ((data.daily_log.calorias_total || 0) / (data.user.meta_calorica?.meta_sugerida || 2000)) * 100,
          pratos: data.daily_log.pratos || [],
          totais: {
            calorias: data.daily_log.calorias_total || 0,
            proteinas: data.daily_log.proteinas_total || 0,
            carboidratos: data.daily_log.carboidratos_total || 0,
            gorduras: data.daily_log.gorduras_total || 0
          }
        });
      } else if (data.ok) {
        setDailySummary({
          nome: data.user.nome,
          meta: data.user.meta_calorica?.meta_sugerida || 2000,
          consumido: 0,
          restante: data.user.meta_calorica?.meta_sugerida || 2000,
          percentual: 0,
          pratos: [],
          totais: { calorias: 0, proteinas: 0, carboidratos: 0, gorduras: 0 }
        });
      }
    } catch (e) {
      console.error('Erro ao carregar resumo:', e);
    }
  };

  // Registrar refeição automaticamente após identificar
  const logMealToPremium = async (prato) => {
    if (!premiumUser || !mountedRef.current) return false;
    
    try {
      const pin = localStorage.getItem('soulnutri_pin');
      if (!pin) return false;
      
      const fd = new FormData();
      fd.append('pin', pin);
      fd.append('prato_nome', prato.dish_display || prato.nome || 'Prato');
      fd.append('calorias', parseFloat(prato.nutrition?.calorias?.replace(/[^\d]/g, '') || prato.calorias?.replace?.(/[^\d]/g, '') || 200));
      fd.append('proteinas', parseFloat(prato.nutrition?.proteinas?.replace(/[^\d]/g, '') || prato.proteinas?.replace?.(/[^\d]/g, '') || 10));
      fd.append('carboidratos', parseFloat(prato.nutrition?.carboidratos?.replace(/[^\d]/g, '') || prato.carboidratos?.replace?.(/[^\d]/g, '') || 25));
      fd.append('gorduras', parseFloat(prato.nutrition?.gorduras?.replace(/[^\d]/g, '') || prato.gorduras?.replace?.(/[^\d]/g, '') || 8));
      fd.append('source', prato.source || 'clip');
      
      console.log('[PREMIUM] Registrando refeição:', prato.dish_display, '| Source:', prato.source || 'clip');
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      const res = await fetch(`${API}/premium/log-meal`, { 
        method: 'POST', 
        body: fd,
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      
      if (!mountedRef.current) return false;
      
      const data = await res.json();
      console.log('[PREMIUM] Resposta log-meal:', data);
      
      if (data.ok) {
        setDailySummary(prev => ({
          ...prev,
          consumido: data.consumido,
          restante: data.restante,
          percentual: data.percentual,
          pratos: [...(prev?.pratos || []), { nome: prato.dish_display, calorias: fd.get('calorias'), hora: new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) }]
        }));
        return true;
      }
      return false;
     } catch (e) {
      if (e?.name === 'AbortError') {
        console.log('[PREMIUM] Registro de refeição abortado (troca de fluxo/componente).');
        return false;
      }
      console.error('Erro ao registrar refeição:', e);
      return false;
    }
  };

  // Logout Premium
  const handlePremiumLogout = () => {
    localStorage.removeItem('soulnutri_pin');
    localStorage.removeItem('soulnutri_nome');
    localStorage.removeItem('soulnutri_user');
    setPremiumUser(null);
    setDailySummary(null);
    setShowPremium(null);
  };

  const startCamera = async () => {
    try {
      setCameraError(null);
      console.log('[Camera] Iniciando...');
      
      // Verificar se componente ainda está montado
      if (!mountedRef.current) {
        console.log('[Camera] Componente desmontado, abortando...');
        return;
      }
      
      // Verificar se mediaDevices está disponível
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        console.error('[Camera] mediaDevices não disponível');
        setCameraError('not_supported');
        return;
      }
      
      // Parar stream anterior se existir (com try-catch para evitar erros)
      try {
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(t => {
            try { t.stop(); } catch(e) { console.warn('[Camera] Erro ao parar track:', e); }
          });
          streamRef.current = null;
        }
      } catch (e) {
        console.warn('[Camera] Erro ao limpar stream anterior:', e);
      }
      
      // Resolucao alta para melhor precisao CLIP (Android respeita literalmente)
      // Safari/Mac: tentar primeiro com facingMode, depois sem
      let s;
      try {
        s = await navigator.mediaDevices.getUserMedia({ 
          video: { 
            facingMode: { ideal: 'environment' }, 
            width: { ideal: 1280, min: 720 }, 
            height: { ideal: 960, min: 540 },
            // Auto-focus continuo - critico para Android
            focusMode: { ideal: 'continuous' },
            // White balance e exposure automaticos
            whiteBalanceMode: { ideal: 'continuous' },
            exposureMode: { ideal: 'continuous' }
          } 
        });
      } catch (firstError) {
        console.warn('[Camera] Falhou com constraints avancados, tentando basico:', firstError);
        // Fallback: sem focus/exposure constraints
        try {
          s = await navigator.mediaDevices.getUserMedia({ 
            video: { 
              facingMode: 'environment',
              width: { ideal: 1280 }, 
              height: { ideal: 960 } 
            } 
          });
        } catch (secondError) {
          console.warn('[Camera] Falhou com facingMode, tentando sem:', secondError);
          try {
            s = await navigator.mediaDevices.getUserMedia({ 
              video: { 
                width: { ideal: 1280 }, 
                height: { ideal: 960 } 
              } 
            });
          } catch (thirdError) {
            console.warn('[Camera] Falhou tudo, tentando basico:', thirdError);
            s = await navigator.mediaDevices.getUserMedia({ video: true });
          }
        }
      }
      
      // Log da resolucao real obtida (diagnostico iOS vs Android)
      const videoTrack = s.getVideoTracks()[0];
      if (videoTrack) {
        const settings = videoTrack.getSettings();
        console.log(`[Camera] Resolucao real: ${settings.width}x${settings.height}, focusMode: ${settings.focusMode || 'N/A'}`);
        
        // Tentar aplicar auto-focus continuo no Android se disponivel
        try {
          const capabilities = videoTrack.getCapabilities?.();
          if (capabilities?.focusMode?.includes('continuous')) {
            await videoTrack.applyConstraints({ advanced: [{ focusMode: 'continuous' }] });
            console.log('[Camera] Auto-focus continuo aplicado');
          }
        } catch (e) {
          console.warn('[Camera] Nao foi possivel aplicar auto-focus:', e);
        }
      }
      
      // Verificar novamente se ainda está montado após await
      if (!mountedRef.current) {
        console.log('[Camera] Componente desmontado durante inicialização, liberando recursos...');
        s.getTracks().forEach(t => t.stop());
        return;
      }
      
      console.log('[Camera] Stream obtido:', s);
      streamRef.current = s;
      setStream(s);
      
      if (videoRef.current) {
        videoRef.current.srcObject = s;
        // Aguardar video estar pronto
        videoRef.current.onloadedmetadata = () => {
          cameraReadyAtRef.current = Date.now();
          console.log('[Camera] Video conectado e pronto');
        };
      }
    } catch (err) { 
      console.error("[Camera] Erro:", err);
      if (mountedRef.current) {
        setCameraError(err.name === 'NotAllowedError' ? 'permission_denied' : 'camera_error');
      }
    }
  };
  
  // Função interna para parar câmera sem depender de state
  const stopCameraInternal = () => {
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => {
          try { t.stop(); } catch(e) { console.warn('[Camera] Erro ao parar track:', e); }
        });
        streamRef.current = null;
      }
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    } catch (e) {
      console.warn('[Camera] Erro ao parar câmera:', e);
    }
  };

  const stopCamera = () => { 
    stopCameraInternal();
    setStream(null);
  };
  
  // Sharpen leve (unsharp mask) para melhorar nitidez em Android
  const sharpenImageData = (imageData, w, h) => {
    const d = imageData.data;
    const copy = new Uint8ClampedArray(d);
    const amount = 0.4; // intensidade do sharpen (0.3-0.6 ideal)
    for (let y = 1; y < h - 1; y++) {
      for (let x = 1; x < w - 1; x++) {
        const idx = (y * w + x) * 4;
        for (let c = 0; c < 3; c++) {
          // Kernel 3x3: centro*5 - vizinhos
          const center = copy[idx + c] * 5;
          const top = copy[((y-1)*w + x)*4 + c];
          const bottom = copy[((y+1)*w + x)*4 + c];
          const left = copy[(y*w + (x-1))*4 + c];
          const right = copy[(y*w + (x+1))*4 + c];
          const sharp = center - top - bottom - left - right;
          d[idx + c] = Math.max(0, Math.min(255, copy[idx + c] + sharp * amount));
        }
      }
    }
  };

  const handleCameraTouch = useCallback(() => {
    // Prevenir cliques múltiplos (debounce de 1s)
    const now = Date.now();
    if (now - lastTouchRef.current < 1000) return;
    lastTouchRef.current = now;
    
    if (loadingRef.current || !videoRef.current || !canvasRef.current) return;
    
    const v = videoRef.current, c = canvasRef.current;
    // Verificar se o vídeo está pronto
    if (v.videoWidth === 0 || v.videoHeight === 0) {
      console.warn('Vídeo ainda não está pronto');
      return;
    }

    // R1 reconhecimento:
    // aguardar autofocus/exposição estabilizarem antes da primeira captura.
    const cameraAgeMs = Date.now() - cameraReadyAtRef.current;
    if (!cameraReadyAtRef.current || cameraAgeMs < 1200) {
      console.warn(`[Camera] Aguardando estabilização: ${cameraAgeMs}ms`);
      return;
    }
    
    // ═══════════════════════════════════════════════════════════════
    // CROP: Capturar APENAS a area dentro da guide-frame (55% x 90%)
    // Isso evita capturar pratos vizinhos fora da moldura
    // ═══════════════════════════════════════════════════════════════
    const guideW = 0.55; // Largura da moldura (55% do container)
    const guideH = 0.90; // Altura da moldura (90% do container)
    
    // Obter dimensoes reais do container de video na tela
    const rect = v.getBoundingClientRect();
    const containerW = rect.width;
    const containerH = rect.height;
    
    // Calcular como object-fit:cover posiciona o video
    const videoAR = v.videoWidth / v.videoHeight;
    const containerAR = containerW / containerH;
    
    let displayW, displayH, offsetX, offsetY;
    if (videoAR < containerAR) {
      // Video mais alto - corta topo/base
      displayW = containerW;
      displayH = containerW / videoAR;
      offsetX = 0;
      offsetY = (displayH - containerH) / 2;
    } else {
      // Video mais largo - corta lados
      displayH = containerH;
      displayW = containerH * videoAR;
      offsetX = (displayW - containerW) / 2;
      offsetY = 0;
    }
    
    // Coordenadas da guide-frame no espaco do container (centralizada)
    const gLeft = containerW * (1 - guideW) / 2;
    const gTop = containerH * (1 - guideH) / 2;
    const gWidth = containerW * guideW;
    const gHeight = containerH * guideH;
    
    // Converter para coordenadas do video original
    const scaleX = v.videoWidth / displayW;
    const scaleY = v.videoHeight / displayH;
    const sx = (gLeft + offsetX) * scaleX;
    const sy = (gTop + offsetY) * scaleY;
    const sw = gWidth * scaleX;
    const sh = gHeight * scaleY;
    
    // R1 reconhecimento:
    // preservar mais detalhe visual no envio.
    // O backend faz o preprocessing final do CLIP em 224x224.
    const maxSize = 768;
    let outW = sw, outH = sh;
    if (outW > maxSize || outH > maxSize) {
      const ratio = Math.min(maxSize / outW, maxSize / outH);
      outW = Math.round(outW * ratio);
      outH = Math.round(outH * ratio);
    }
    
    c.width = outW;
    c.height = outH;
    const ctx = c.getContext('2d');
    // drawImage(source, sx, sy, sWidth, sHeight, dx, dy, dWidth, dHeight)
    ctx.drawImage(v, sx, sy, sw, sh, 0, 0, outW, outH);
    console.log(`[Capture] Crop: video(${v.videoWidth}x${v.videoHeight}) guide(${Math.round(sx)},${Math.round(sy)} ${Math.round(sw)}x${Math.round(sh)}) -> canvas(${outW}x${outH})`);
    
    // Qualidade 82% - alinhado R3 (camera/scanner/galeria uniformizados)
    c.toBlob(b => {
      if (b && mountedRef.current) {
        setLastImageBlob(b);
        identifyImage(b, { width: outW, height: outH });
      }
      // Limpar canvas após uso
      ctx.clearRect(0, 0, outW, outH);
    }, 'image/jpeg', 0.92);
  }, [multiMode, detectedRestaurant]);

  const normalizeResult = (raw) => {
    if (!raw) return null;

    return {
      ...raw,

      category:
        raw.category ||
        raw.categoria ||
        "não classificado",

      nutrition: raw.nutrition || null,

      calorias:
        raw.nutrition?.calorias ||
        raw.calorias ||
        null,

      proteinas:
        raw.nutrition?.proteinas ||
        raw.proteinas ||
        null,

      carboidratos:
        raw.nutrition?.carboidratos ||
        raw.carboidratos ||
        null,

      gorduras:
        raw.nutrition?.gorduras ||
        raw.gorduras ||
        null,

      ingredientes: raw.ingredientes || [],
      beneficios: raw.beneficios || [],
      riscos: raw.riscos || [],

      dish: raw.dish || "",
      dish_display: raw.dish_display || raw.dish || ""
    };
  };

  const identifyImage = async (blob, imgDims = null) => {
    const _scanId = ++_ROOT_SCAN_COUNTER;
    console.log(`[FINAL_PROOF] scan_id=${_scanId} IDENTIFY_ENTER premiumBusy=${premiumCycleBusyRef.current} premiumUser=${!!premiumUser}`);

    // ── DIAGNÓSTICO (read-only, não altera lógica funcional) ──────────────
    const DIAG = process.env.REACT_APP_DIAG_LOGS === 'true';
    const _requestId = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

  if (premiumUser && premiumCycleBusyRef.current) {
    console.log(`[FINAL_PROOF] scan_id=${_scanId} GATE_BLOCKED before_fetch premiumBusy=true`);
    return;
  }

  if (premiumUser) {
    premiumCycleBusyRef.current = true;
  }
    // Cancelar requisição anterior se existir
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    loadingRef.current = true;
    setLoading(true);
    setResult(null);
    setMultiResult(null);
    setError(null);
    setFeedbackSent(false);
    setShowCorrectionFlow(false);
    setCorrectionSearch('');
    setShowFeedback(false);
    
    const fd = new FormData(); 
    fd.append("file", blob, "photo.jpg");
    fd.append("country", "BR");
    const restaurantValue = getRestaurantValue(); // chamada única, reutilizada abaixo
    fd.append("restaurant", restaurantValue);

    // ── DIAGNÓSTICO: headers de correlação (gated, sem PII/GPS) ───────────
    let _restaurantSource = 'default';
    if (detectedRestaurant === 'cibi_sana' || detectedRestaurant === 'external') {
      _restaurantSource = 'gps';
    } else if (localStorage.getItem('soulnutri_location_manual') === 'true') {
      _restaurantSource = 'manual';
    }
    const _clientMode = isStandalone ? 'standalone' : 'browser';
    const diagHeaders = { 'X-Request-ID': _requestId };
    if (DIAG) {
      diagHeaders['X-Client-Mode'] = _clientMode;
      diagHeaders['X-Client-Version'] = process.env.REACT_APP_BUILD_ID || 'unknown';
      diagHeaders['X-Restaurant-Raw'] = String(detectedRestaurant);
      diagHeaders['X-Restaurant-Normalized'] = restaurantValue;
      diagHeaders['X-Restaurant-Source'] = _restaurantSource;
      diagHeaders['X-Image-Mime'] = blob.type || 'unknown';
      diagHeaders['X-Image-Bytes'] = String(blob.size || 0);
      if (imgDims && imgDims.width && imgDims.height) {
        diagHeaders['X-Image-Width'] = String(imgDims.width);
        diagHeaders['X-Image-Height'] = String(imgDims.height);
      }
      console.log(`[SCAN_START] request_id=${_requestId} client_mode=${_clientMode} client_version=${process.env.REACT_APP_BUILD_ID || 'unknown'} restaurant=${restaurantValue} restaurant_source=${_restaurantSource} blob_type=${blob.type} blob_size=${blob.size} dims=${imgDims ? `${imgDims.width}x${imgDims.height}` : 'n/a'}`);
    }
    
    // Se for Premium, enviar credenciais para receber dados exclusivos
    try {
      const pin = localStorage.getItem('soulnutri_pin');
      const nome = localStorage.getItem('soulnutri_nome');
      if (pin && nome && !multiMode) {
        fd.append("pin", pin);
        fd.append("nome", nome);
      }
    } catch (e) {
      console.warn('localStorage não disponível:', e);
    }
    
    // Criar AbortController com timeout
    abortControllerRef.current = new AbortController();
    const timeoutId = setTimeout(() => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    }, REQUEST_TIMEOUT);
    
    try {
      const t = Date.now();
      const endpoint = multiMode ? `${API}/ai/identify-multi` : `${API}/ai/identify`;
      console.log(`[FINAL_PROOF] scan_id=${_scanId} FETCH_START identify`);
      const res = await fetch(endpoint, { 
        method: "POST", 
        body: fd,
        signal: abortControllerRef.current.signal,
        headers: diagHeaders
      });
      clearTimeout(timeoutId);
      const reqMs = Date.now() - t;
      console.log(`[FINAL_PROOF] scan_id=${_scanId} FETCH_RESPONSE status=${res.status} time=${reqMs}ms restaurant=${fd.get('restaurant')} backend=${API}`);
      if (DIAG) {
        console.log(`[SCAN_RESPONSE] request_id=${_requestId} status=${res.status} content_type=${res.headers.get('content-type')} duration_ms=${reqMs}`);
      }
      
      if (!mountedRef.current) return;
      
      // Verificar se a resposta foi bem sucedida
      if (!res.ok) {
        throw new Error('Servidor temporariamente indisponivel. Tente novamente.');
      }
      
      let data;
      try {
        data = await res.json();
      } catch {
        throw new Error('Servidor reiniciando. Aguarde e tente novamente.');
      }
      
      if (DIAG) {
        console.log(`[SCAN_RESPONSE] request_id=${_requestId} ok=${data.ok} identified=${data.identified} confidence=${data.confidence} source=${data.source} reason=${data.message || 'n/a'}`);
      }
      
      if (multiMode) {
        setMultiResult({ ...data, totalTime: Date.now() - t });
      } else {
        const resultWithTime = { ...data, totalTime: Date.now() - t };
        // [AUDITORIA] Log completo do payload real para confirmar dish_display no celular
        console.log(`[FINAL_PROOF] scan_id=${_scanId} DISH_DISPLAY="${data.dish_display}" dish_raw="${data.dish}" identified=${data.identified} source=${data.source} total_ms=${resultWithTime.totalTime}`);
        setResult(normalizeResult(resultWithTime));
        setLoading(false); // Mostrar resultado IMEDIATAMENTE
        loadingRef.current = false;
        
        // Salvar foto na galeria se identificou com sucesso
        if (resultWithTime.ok && resultWithTime.identified && previewImageUrl) {
          saveToGallery(previewImageUrl, resultWithTime.dish_display, resultWithTime);
        }
        // Buscar informações do Radar e Enrich em BACKGROUND (sem bloquear UI)
        if (resultWithTime.ok && resultWithTime.identified) {
          // Radar - fire and forget, somente Premium
          const ingredientes = resultWithTime.ingredientes?.join(',') || '';
          if (resultWithTime.is_premium) {
            const radarPin = localStorage.getItem('soulnutri_pin') || '';
            const radarNome = localStorage.getItem('soulnutri_nome') || '';
            fetch(`${API}/radar/alimentos/${encodeURIComponent(resultWithTime.dish_display)}?ingredientes=${encodeURIComponent(ingredientes)}`, {
              headers: {
                'X-SoulNutri-Pin': radarPin,
                'X-SoulNutri-Nome': radarNome,
              },
            })
            .then(r => r.json())
            .then(radarData => {
              if (radarData.ok && radarData.radar && 
                  (radarData.radar.has_alert || radarData.radar.voce_sabia || radarData.radar.combinacoes?.length > 0)) {
                setRadarInfo(radarData.radar);
              } else {
                setRadarInfo(null);
              }
            })
            .catch(() => setRadarInfo(null));
          } else {
            setRadarInfo(null);
          }
          
          // ENRICH: movido para useEffect dedicado (evita stale closure)
        }
        // NÃO mostrar modal automaticamente - deixar usuário ver as informações primeiro
      }
    } catch (e) { 
      clearTimeout(timeoutId);
      if (!mountedRef.current) return;
      console.log(`[FINAL_PROOF] scan_id=${_scanId} FETCH_ERROR name=${e.name} message=${e.message}`);
      if (DIAG) {
        console.log(`[SCAN_ERROR] request_id=${_requestId} error_name=${e.name} error_message=${e.message} step=fetch_or_parse abort=${e.name === 'AbortError'}`);
      }
      
      if (e.name === 'AbortError') {
        setError('Tempo limite excedido. Tente novamente.');
      } else if (e.message && (e.message.includes('postMessage') || e.message.includes('body stream'))) {
        // Erro de script externo (emergent-main.js) - ignorar
        console.warn('[IDENTIFY] Erro de script externo ignorado:', e.message);
      } else {
        setError(e.message || 'Erro de conexão');
      }
    } finally { 
      // Garantir que loading é desligado em caso de erro
      if (loadingRef.current) {
        loadingRef.current = false;
        if (mountedRef.current) {
          setLoading(false);
        }
      }
      // Reset do guard premium em TODOS os caminhos (sucesso, erro, timeout, abort)
      // Sem este reset, qualquer scan com falha trava permanentemente o próximo scan
      premiumCycleBusyRef.current = false;
    }
  };

  // Fluxo Único: Adicionar item atual à lista e preparar para próximo
  const addItemToPlate = async () => {
    // 🔴 CANCELAR REQUEST PENDENTE
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    // 🔴 RESETAR FLAGS CRÍTICAS
    setLoading(false);
    

      if (premiumUser) {
        premiumCycleBusyRef.current = false;
      }

    // 🔴 LIMPAR ESTADOS RESIDUAIS
    setScannerResult(null);

    if (result?.ok && result?.identified) {
      const newItem = {
        id: Date.now(),
        dish: result.dish,
        dish_display: result.dish_display,
        category: result.category,
        calorias: result.nutrition?.calorias 
          ? result.nutrition.calorias 
          : (result.calorias_estimadas || '—'),
        proteinas: result.nutrition?.proteinas 
          ? result.nutrition.proteinas 
          : '—',
        carboidratos: result.nutrition?.carboidratos 
          ? result.nutrition.carboidratos 
          : '—',
        gorduras: result.nutrition?.gorduras 
          ? result.nutrition.gorduras 
          : '—',
        ingredientes: result.ingredientes || [],
        beneficios: result.beneficios || [],
        riscos: result.riscos || [],
        alergenos: {
          gluten: result.contem_gluten || result.alergenos?.gluten,
          lactose: result.contem_lactose || result.alergenos?.lactose,
          ovo: result.contem_ovo || result.alergenos?.ovo,
          castanhas: result.contem_castanhas || result.alergenos?.castanhas,
          frutosMar: result.contem_frutos_mar || result.alergenos?.frutos_mar
        },
        score: result.score,
        source: result.source || 'clip',
        // Premium data
        curiosidade: result.curiosidade || result.curiosidade_cientifica || '',
        combinacoes: result.combinacoes || result.premium?.combinacoes_sugeridas || [],
        beneficio_principal: result.beneficio_principal || '',
        alerta_saude: result.alerta_saude || '',
        mito_verdade: result.mito_verdade || null,
        voce_sabia: result.voce_sabia || '',
        dica_chef: result.dica_chef || '',
        premium: result.premium || null,
        alertas_personalizados: result.alertas_personalizados || [],
        noticias: result.noticias || [],
        contextual_breaking_news: result.contextual_breaking_news || null,
      };

      setPlateItems(prev => {
        // Evitar duplicatas pelo nome do prato
        if (prev.some(item => item.dish_display === newItem.dish_display)) {
          console.log('[PLATE] Prato já no prato:', newItem.dish_display);
          return prev;
        }
        const updated = [...prev, newItem];
        console.log('[PLATE] Adicionado:', newItem.dish_display, '| Total:', updated.length);
        return updated;
      });
      
      // Registrar no contador Premium se usuário estiver logado
      if (premiumUser) {
        const registered = await logMealToPremium({
          dish_display: newItem.dish_display,
          nutrition: {
            calorias: newItem.calorias,
            proteinas: newItem.proteinas,
            carboidratos: newItem.carboidratos,
            gorduras: newItem.gorduras
          },
          source: newItem.source
        });
        if (registered) {
          setMealRegistered(true);
          // Esconder após 3 segundos
          setTimeout(() => setMealRegistered(false), 3000);
        }
      }
    }
    setShowAddMore(false);
    setResult(null);
    setPreviewImageUrl(null);
  };

  // Fluxo Único: Finalizar prato e mostrar resumo consolidado
  const finishPlate = async () => {
    console.log('[DEBUG] finishPlate chamado, result:', result?.dish_display);
    console.log('[DEBUG] plateItems antes:', plateItems.length, 'itens');
    
    if (result?.ok && result?.identified) {
      // Adicionar último item se houver
      const newItem = {
        id: Date.now(),
        dish: result.dish,
        dish_display: result.dish_display,
        category: result.category,
        calorias: result.nutrition?.calorias || result.calorias_estimadas || '0 kcal',
        proteinas: result.nutrition?.proteinas || '0g',
        carboidratos: result.nutrition?.carboidratos || '0g',
        gorduras: result.nutrition?.gorduras || '0g',
        ingredientes: result.ingredientes || [],
        beneficios: result.beneficios || [],
        riscos: result.riscos || [],
        alergenos: {
          gluten: result.contem_gluten || result.alergenos?.gluten,
          lactose: result.contem_lactose || result.alergenos?.lactose,
          ovo: result.contem_ovo || result.alergenos?.ovo,
          castanhas: result.contem_castanhas || result.alergenos?.castanhas,
          frutosMar: result.contem_frutos_mar || result.alergenos?.frutos_mar
        },
        score: result.score,
        source: result.source || 'clip',
        // Premium data
        curiosidade: result.curiosidade || result.curiosidade_cientifica || '',
        combinacoes: result.combinacoes || result.premium?.combinacoes_sugeridas || [],
        beneficio_principal: result.beneficio_principal || '',
        alerta_saude: result.alerta_saude || '',
        mito_verdade: result.mito_verdade || null,
        voce_sabia: result.voce_sabia || '',
        dica_chef: result.dica_chef || '',
        premium: result.premium || null,
        alertas_personalizados: result.alertas_personalizados || [],
        noticias: result.noticias || [],
        contextual_breaking_news: result.contextual_breaking_news || null,
      };

      setPlateItems(prev => {
        const updated = [...prev, newItem];
        console.log('[DEBUG] plateItems final:', updated.length, 'itens');
        return updated;
      });
      
      // Registrar no histórico Premium
      if (premiumUser) {
        await logMealToPremium({
          dish_display: newItem.dish_display,
          nutrition: {
            calorias: newItem.calorias,
            proteinas: newItem.proteinas,
            carboidratos: newItem.carboidratos,
            gorduras: newItem.gorduras
          },
          source: newItem.source
        });
      }
    }
    setShowAddMore(false);
    setResult(null);
    setViewMode('mesa'); // Mudar para vista consolidada
  };

  // Fluxo Único: Limpar prato e começar novo
  const clearPlate = () => {
    setPlateItems([]);
    setResult(null);
    setMultiResult(null);
    setPreviewImageUrl(null);
    setShowAddMore(false);
    setScannerResult(null);
    // Limpar estados residuais
    setEnrichLoading(false);
    setError(null);
    setTtsLoading(false);
    setTtsError(null);
    setRadarInfo(null);
    // Resetar controle de enrich para permitir re-scan do mesmo prato
    enrichedDishesRef.current.clear();
    if (ttsAudioRef.current) {
      ttsAudioRef.current.pause();
      ttsAudioRef.current = null;
      setTtsPlaying(false);
    }
    localStorage.removeItem('soulnutri_plate_items');
    localStorage.removeItem('soulnutri_view_mode');
  };

  // ═══════════════════════════════════════════════════════════════
  // GALERIA - Salvar e visualizar fotos capturadas
  // ═══════════════════════════════════════════════════════════════
  
  const saveToGallery = (imageUrl, dishName, result) => {
    const newPhoto = {
      id: Date.now(),
      imageUrl,
      dishName: dishName || 'Prato não identificado',
      date: new Date().toLocaleString('pt-BR'),
      result: result ? {
        calorias: result.nutrition?.calorias || result.calorias_estimadas,
        categoria: result.category
      } : null
    };
    
    setPhotoGallery(prev => {
      const updated = [newPhoto, ...prev].slice(0, 50); // Máximo 50 fotos
      localStorage.setItem('soulnutri_gallery', JSON.stringify(updated));
      return updated;
    });
  };
  
  const deleteFromGallery = (photoId) => {
    setPhotoGallery(prev => {
      const updated = prev.filter(p => p.id !== photoId);
      localStorage.setItem('soulnutri_gallery', JSON.stringify(updated));
      return updated;
    });
  };
  
  const clearGallery = () => {
    if (window.confirm('Apagar todas as fotos da galeria?')) {
      setPhotoGallery([]);
      localStorage.removeItem('soulnutri_gallery');
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // MODO SCANNER CONTÍNUO - Detecta mudança de imagem e identifica
  // ═══════════════════════════════════════════════════════════════
  
  const lastFrameDataRef = useRef(null);
  const scanningRef = useRef(false);
  const lastScanResultRef = useRef(null); // Cache do último resultado
const scanInFlightRef = useRef(false);  // trava anti-reentrada do scanner
  const scanCooldownRef = useRef(false); // Cooldown entre scans
  
  // Função para calcular diferença entre frames
  const calculateFrameDifference = (currentData, previousData) => {
    if (!previousData || currentData.length !== previousData.length) return 1;
    
    let diff = 0;
    const sampleSize = Math.min(1000, currentData.length / 4); // Amostra de pixels
    const step = Math.floor(currentData.length / 4 / sampleSize);
    
    for (let i = 0; i < currentData.length; i += step * 4) {
      diff += Math.abs(currentData[i] - previousData[i]); // R
      diff += Math.abs(currentData[i + 1] - previousData[i + 1]); // G
      diff += Math.abs(currentData[i + 2] - previousData[i + 2]); // B
    }
    
    return diff / (sampleSize * 3 * 255); // Normalizado 0-1
  };

  const performScan = useCallback(async () => {
    // FLAG: auto-scan desativado temporariamente para isolamento de diagnóstico
    // Apenas scan manual (captureManual) gera resultado. Não remover este bloco.
    return; // AUTOSCAN_DISABLED
    // Não escanear se já está escaneando, carregando, em cooldown, ou sem câmera
    if (scanInFlightRef.current) return; // eslint-disable-line no-unreachable
if (scanningRef.current || loadingRef.current || scanCooldownRef.current || !videoRef.current || !canvasRef.current || !stream) return;

scanInFlightRef.current = true;

try {
    
    const v = videoRef.current;
    const c = canvasRef.current;
    
    // Verificar se o vídeo está pronto
    if (v.videoWidth === 0 || v.videoHeight === 0) return;
    
    // Tamanho pequeno para detecção de mudança (rápido)
    const detectSize = 160;
    c.width = detectSize;
    c.height = detectSize;
    const ctx = c.getContext('2d');
    ctx.drawImage(v, 0, 0, detectSize, detectSize);
    
    // Obter dados do frame atual
    const imageData = ctx.getImageData(0, 0, detectSize, detectSize);
    const currentData = imageData.data;
    
    // Calcular diferença com frame anterior
    const difference = calculateFrameDifference(currentData, lastFrameDataRef.current);
    
    // Se mudança significativa (> 30%), fazer reconhecimento
    // Threshold maior = menos scans desnecessários
    if (difference > 0.30) {
      // Salvar frame atual
      lastFrameDataRef.current = new Uint8ClampedArray(currentData);
      
      // Ativar cooldown (evita múltiplos scans seguidos)
      scanCooldownRef.current = true;
      setTimeout(() => { scanCooldownRef.current = false; }, 2000);
      
      // ═══ CROP: Capturar apenas area da guide-frame ═══
      const guideW = 0.55, guideH = 0.90;
      const rect = v.getBoundingClientRect();
      const cW = rect.width, cH = rect.height;
      const vAR = v.videoWidth / v.videoHeight;
      const cAR = cW / cH;
      let dW, dH, oX, oY;
      if (vAR < cAR) { dW = cW; dH = cW / vAR; oX = 0; oY = (dH - cH) / 2; }
      else { dH = cH; dW = cH * vAR; oX = (dW - cW) / 2; oY = 0; }
      const sX = ((cW * (1 - guideW) / 2) + oX) * (v.videoWidth / dW);
      const sY = ((cH * (1 - guideH) / 2) + oY) * (v.videoHeight / dH);
      const sW = (cW * guideW) * (v.videoWidth / dW);
      const sH = (cH * guideH) * (v.videoHeight / dH);
      
      const scanSize = 512;
      let w = sW, h = sH;
      if (w > scanSize || h > scanSize) {
        const ratio = Math.min(scanSize / w, scanSize / h);
        w = Math.round(w * ratio);
        h = Math.round(h * ratio);
      }
      
      c.width = w;
      c.height = h;
      ctx.drawImage(v, sX, sY, sW, sH, 0, 0, w, h);
      
      // Qualidade 82% alinhada R3 (uniformizada com camera/galeria)
      c.toBlob(async (blob) => {
        if (!blob || !mountedRef.current || scanningRef.current) return;
        
        scanningRef.current = true;
        
        try {
          const fd = new FormData();
          fd.append("file", blob, "scan.jpg");
          fd.append("country", "BR");
          fd.append("restaurant", getRestaurantValue());
          
          const res = await fetch(`${API}/ai/identify`, {
            method: "POST",
            body: fd
          });
          
          if (!mountedRef.current) return;
          
          let data;
          try { data = await res.json(); } catch { return; }
          
          // Só mostrar se score >= 0.7 (mais confiável)
          if (data.ok && data.identified && data.score >= 0.7) {
            // Verificar se é diferente do último resultado (evita repetições)
            const newDish = data.dish_display;
            if (newDish !== lastScanResultRef.current) {
              lastScanResultRef.current = newDish;
              setScannerResult({
                dish: data.dish,
                dish_display: data.dish_display,
                categoria: data.category || data.categoria,
                calorias: data.nutrition?.calorias || '~150 kcal',
                score: data.score,
                confidence: data.confidence,
                beneficios: data.beneficios || [],
                riscos: data.riscos || [],
                contem_gluten: data.contem_gluten,
                timestamp: Date.now()
              });
            }
          } else if (difference > 0.5) {
            // Mudança muito grande mas não identificado - limpar resultado anterior
            setScannerResult(null);
            lastScanResultRef.current = null;
          }
        } catch (e) {
          console.warn('Scanner error:', e);
        } finally {
          scanningRef.current = false;
        }
        
        // Limpar canvas
        ctx.clearRect(0, 0, w, h);
      }, 'image/jpeg', 0.82);
    }
    } finally {
      scanInFlightRef.current = false;
    }
  }, [stream]);

  // Scanner NÃO pode ser disparado automaticamente por useEffect
  useEffect(() => {
    if (scannerMode && stream) {
      // apenas garantir que a câmera/scanner está pronta
      // NÃO executar performScan automaticamente
    }

    return () => {
      if (scanIntervalRef.current) {
        clearInterval(scanIntervalRef.current);
        scanIntervalRef.current = null;
      }
    };
  }, [scannerMode, stream]);

  // Toque no overlay para ver detalhes completos
  const handleScannerTap = useCallback(() => {
    if (scannerResult) {
      // Converter scanner result para result completo
      setResult(normalizeResult({
        ok: true,
        identified: true,
        ...scannerResult
      }));
      setScannerResult(null);
    }
  }, [scannerResult]);

  // Calcular dados CONSOLIDADOS do prato (para vista "mesa")
  const plateConsolidated = useMemo(() => {
    if (plateItems.length === 0) return null;
    
    // Função para extrair número de string tipo "120 kcal" ou "15g"
    const parseNum = (val) => {
      if (typeof val === 'number') return val;
      if (typeof val === 'string') return parseFloat(val.replace(/[^\d.]/g, '')) || 0;
      return 0;
    };
    
    // Somar nutrientes
    const totals = plateItems.reduce((acc, item) => ({
      calorias: acc.calorias + parseNum(item.calorias),
      proteinas: acc.proteinas + parseNum(item.proteinas),
      carboidratos: acc.carboidratos + parseNum(item.carboidratos),
      gorduras: acc.gorduras + parseNum(item.gorduras)
    }), { calorias: 0, proteinas: 0, carboidratos: 0, gorduras: 0 });
    
    // Coletar todos os ingredientes únicos
    const allIngredientes = [...new Set(plateItems.flatMap(item => item.ingredientes || []))];
    
    // Coletar benefícios únicos (máx 5)
    const allBeneficios = [...new Set(plateItems.flatMap(item => item.beneficios || []))].slice(0, 5);
    
    // Coletar riscos únicos (máx 3)
const allRiscos = [...new Set(plateItems.flatMap(item => item.riscos || []))].slice(0, 3);

// Verificar alérgenos presentes em qualquer item (objeto alergenos ou texto nos riscos)
const riscosLower = safeText(allRiscos.join(' '));
const contemGluten = plateItems.some(item => item.alergenos?.gluten) ||
                     riscosLower.includes('glúten') || riscosLower.includes('gluten');
const contemLactose = plateItems.some(item => item.alergenos?.lactose) ||
                      riscosLower.includes('lactose') || riscosLower.includes('leite');
const contemOvo = plateItems.some(item => item.alergenos?.ovo) ||
                  riscosLower.includes('ovo');
const contemCastanhas = plateItems.some(item => item.alergenos?.castanhas) ||
                        riscosLower.includes('castanha') || riscosLower.includes('amendoim') || riscosLower.includes('nozes');
const contemFrutosMar = plateItems.some(item => item.alergenos?.frutosMar) ||
                        riscosLower.includes('peixe') || riscosLower.includes('camarão') ||
                        riscosLower.includes('crustáceo') || riscosLower.includes('frutos do mar');
const contemSoja = plateItems.some(item => item.alergenos?.soja) ||
                   riscosLower.includes('soja');

// Filtrar riscos que NÃO são sobre alérgenos (para evitar duplicação)
const riscosNaoAlergenos = allRiscos.filter((r) => {
  const lower = safeText(r);
  return !(lower.includes('alérgeno') || lower.includes('glúten') || lower.includes('lactose') ||
           lower.includes('ovo') || lower.includes('castanha') || lower.includes('peixe') ||
           lower.includes('camarão') || lower.includes('soja'));
});

// Categorias presentes
const categorias = [...new Set(plateItems.map(item => item.category).filter(Boolean))];

return {
  itens: plateItems.map(i => i.dish_display),
  totalItens: plateItems.length,
  nutrition: {
    calorias: `${Math.round(totals.calorias)} kcal`,
    proteinas: `${Math.round(totals.proteinas)}g`,
    carboidratos: `${Math.round(totals.carboidratos)}g`,
    gorduras: `${Math.round(totals.gorduras)}g`
  },
  ingredientes: allIngredientes,
  beneficios: allBeneficios,
      riscos: riscosNaoAlergenos.length > 0 ? riscosNaoAlergenos : allRiscos.slice(0, 2),
      contemGluten,
      contemLactose,
      contemOvo,
      contemCastanhas,
      contemFrutosMar,
      contemSoja,
      categorias,
      // Premium consolidated data
      curiosidades: [...new Set(plateItems.map(item => item.curiosidade).filter(Boolean))],
      combinacoes: [...new Set(plateItems.flatMap(item => item.combinacoes || []))].slice(0, 6),
      beneficio_principal: [...new Set(plateItems.map(item => item.beneficio_principal).filter(Boolean))],
      alerta_saude: [...new Set(plateItems.map(item => item.alerta_saude).filter(Boolean))],
      mitos_verdades: plateItems.map(item => item.mito_verdade).filter(Boolean)
        .filter((mv, i, arr) => arr.findIndex(x => (x.mito || x.afirmacao) === (mv.mito || mv.afirmacao)) === i),
      voce_sabia: [...new Set(plateItems.map(item => item.voce_sabia).filter(Boolean))],
      dicas_chef: [...new Set(plateItems.map(item => item.dica_chef).filter(Boolean))],
      alertas_personalizados: plateItems.flatMap(item => item.alertas_personalizados || [])
        .filter((a, i, arr) => arr.findIndex(x => (x.mensagem || x) === (a.mensagem || a)) === i),
      noticias: [...new Set(plateItems.flatMap(item => item.noticias || []))],
      contextual_breaking_news_items: plateItems
        .map(item => item.contextual_breaking_news)
        .filter(Boolean),
      premiumData: plateItems.map(item => item.premium).filter(Boolean)
    };
  }, [plateItems]);

  // Totais simples para exibição rápida
  const plateTotals = {
    calorias: plateConsolidated?.nutrition?.calorias ? parseFloat(plateConsolidated.nutrition.calorias) : 0,
    itens: plateItems.length
  };

  // Normalizar imagem para R3: 512px max, JPEG 82% (alinhado camera/scanner)
  const normalizeImage = (file) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const maxSize = 224; // CLIP nativo (servidor redimensiona para 224 internamente)
        let w = img.width;
        let h = img.height;
        if (w > maxSize || h > maxSize) {
          const ratio = Math.min(maxSize / w, maxSize / h);
          w = Math.round(w * ratio);
          h = Math.round(h * ratio);
        }
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);
        canvas.toBlob((blob) => {
          resolve(blob || file);
        }, 'image/jpeg', 0.82);
      };
      img.onerror = () => resolve(file);
      img.src = URL.createObjectURL(file);
    });
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      setPreviewImageUrl(previewUrl);
      // Normalizar resolucao e qualidade antes de enviar
      const normalized = await normalizeImage(file);
      setLastImageBlob(normalized);
      identifyImage(normalized);
    }
  };

  const clearResult = () => {
    // Cancelar requisições pendentes
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setResult(null);
    setMultiResult(null);
    setError(null);
    setShowFeedback(false);
    setFeedbackSent(false);
    setShowCorrectionFlow(false);
    setCorrectionSearch('');
    // Limpar preview da imagem
    if (previewImageUrl) {
      URL.revokeObjectURL(previewImageUrl);
      setPreviewImageUrl(null);
    }
    // Liberar memória do blob
    if (lastImageBlob) {
      URL.revokeObjectURL(URL.createObjectURL(lastImageBlob));
    }
    setLastImageBlob(null);
    setNewDishName("");
    setSearchFilter("");
  };

  // Registrar amostra de calibracao (chamado por Sim e Nao)
  const logCalibrationSample = async (isCorrect, dishReal = '') => {
    const scoreVal = result?.score;
    const dishClip = result?.dish || "";
    console.log('[CALIBRATION] Tentando registrar:', { isCorrect, dishClip, dishReal, scoreVal, hasResult: !!result });
    
    if (scoreVal === undefined || scoreVal === null) {
      console.warn('[CALIBRATION] Score nao disponivel, abortando');
      return;
    }
    
    try {
      const fd = new FormData();
      fd.append("dish_clip", dishClip);
      fd.append("dish_real", isCorrect ? dishClip : (dishReal || dishClip));
      fd.append("is_correct", isCorrect ? "true" : "false");
      fd.append("score", String(scoreVal));
      fd.append("confidence", result?.confidence || "");
      fd.append("source", result?.source || "");
      const res = await fetch(`${API}/ai/calibration/log`, { method: "POST", body: fd });
      const data = await res.json();
      console.log('[CALIBRATION] Resposta:', data);
    } catch (e) {
      console.error('[CALIBRATION] Erro ao registrar:', e);
    }
  };

  // Enviar feedback - CORRETO
  const sendFeedbackCorrect = async () => {
    logCalibrationSample(true);
    setFeedbackSent(true);
    setShowFeedback(false);
    
    if (!lastImageBlob || !result?.dish) return;
    
    const fd = new FormData();
    fd.append("file", lastImageBlob, "photo.jpg");
    fd.append("dish_slug", result.dish);
    fd.append("is_correct", "true");
    fd.append("score", String(result.score || 0));
    fd.append("confidence", result.confidence || "");
    fd.append("source", result.source || "");
    
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);
      await fetch(`${API}/ai/feedback`, { 
        method: "POST", 
        body: fd,
        signal: controller.signal
      });
      clearTimeout(timeoutId);
    } catch (e) {
      console.error('Erro ao enviar feedback:', e);
    }
  };

  // Enviar para fila de moderação - Usuário reporta que está incorreto
  const sendToModerationQueue = async () => {
    if (!result) return;
    
    // Mostrar fluxo de correção em vez de enviar direto
    setShowCorrectionFlow(true);
  };

  // Correção: Usuário seleciona o prato correto da lista
  const sendCorrectionWithDish = async (dishName) => {
    if (!result) return;
    
    // Registrar na calibracao como INCORRETO com prato real
    logCalibrationSample(false, dishName);
    
    // Enviar para moderação se tiver imagem
    if (lastImageBlob) {
      try {
        const fd = new FormData();
        fd.append("file", lastImageBlob, "photo.jpg");
        fd.append("original_dish", result?.dish || "");
        fd.append("original_dish_display", result?.dish_display || "");
        fd.append("confidence", result?.confidence || "");
        fd.append("score", result?.score || 0);
        fd.append("source", result?.source || "");
        fd.append("correct_dish", dishName);
        await fetch(`${API}/feedback/moderation-queue`, { method: "POST", body: fd });
      } catch (e) { /* silent */ }
    }
    
    setFeedbackSent(true);
    setShowCorrectionFlow(false);
    setCorrectionSearch('');
  };

  // Correção: Prato não está cadastrado
  const sendCorrectionNotInList = async () => {
    if (!result) return;
    
    // Registrar na calibracao como NAO_CADASTRADO
    logCalibrationSample(false, "NAO_CADASTRADO");
    
    // Enviar para moderação se tiver imagem
    if (lastImageBlob) {
      try {
        const fd = new FormData();
        fd.append("file", lastImageBlob, "photo.jpg");
        fd.append("original_dish", result?.dish || "");
        fd.append("original_dish_display", result?.dish_display || "");
        fd.append("confidence", result?.confidence || "");
        fd.append("score", result?.score || 0);
        fd.append("source", result?.source || "");
        fd.append("correct_dish", "NAO_CADASTRADO");
        await fetch(`${API}/feedback/moderation-queue`, { method: "POST", body: fd });
      } catch (e) { /* silent */ }
    }
    
    setFeedbackSent(true);
    setShowCorrectionFlow(false);
    setCorrectionSearch('');
  };

  // SALVAR CORREÇÃO DE PRATO MÚLTIPLO
  const saveMultiCorrection = async () => {
    if (!lastImageBlob || !multiCorrections.principal.trim()) {
      alert('Por favor, informe pelo menos o prato principal');
      return;
    }
    
    setCreatingDish(true);
    
    try {
      // Converter blob para array buffer para evitar "body already read"
      const arrayBuffer = await lastImageBlob.arrayBuffer();
      const blob = new Blob([arrayBuffer], { type: 'image/jpeg' });
      
      // Salvar prato principal
      const fdPrincipal = new FormData();
      fdPrincipal.append("file", blob, "photo.jpg");
      fdPrincipal.append("dish_name", multiCorrections.principal.trim());
      
      // Adicionar acompanhamentos se existirem
      if (multiCorrections.acompanhamentos.trim()) {
        fdPrincipal.append("acompanhamentos", multiCorrections.acompanhamentos.trim());
      }
      
      const res = await fetch(`${API}/ai/create-dish`, { 
        method: "POST", 
        body: fdPrincipal
      });
      
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      
      const data = await res.json();
      
      if (data.ok) {
        const pratoNome = multiCorrections.principal;
        setFeedbackSent(true);
        setShowMultiCorrection(false);
        setMultiCorrections({ principal: '', acompanhamentos: '' });
        alert(`✅ Prato "${pratoNome}" salvo com sucesso!\n\nA IA vai aprender com esta correção.`);
        loadDishes(); // Atualizar lista
      } else {
        alert(data.error || 'Erro ao salvar correção');
      }
    } catch (e) {
      console.error('Erro ao salvar correção:', e);
      alert('Erro ao salvar: ' + (e.message || 'Tente novamente'));
    } finally {
      setCreatingDish(false);
    }
  };

  // CRIAR PRATO NOVO - VERSÃO LOCAL (SEM CRÉDITOS)
  const createNewDish = async () => {
    if (!lastImageBlob || !newDishName.trim()) return;
    
    setCreatingDish(true);
    
    const fd = new FormData();
    fd.append("file", lastImageBlob, "photo.jpg");
    fd.append("dish_name", newDishName.trim());
    
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);
      
      // USAR ENDPOINT LOCAL (SEM CRÉDITOS)
      const res = await fetch(`${API}/ai/create-dish-local`, { 
        method: "POST", 
        body: fd,
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      
      if (!mountedRef.current) return;
      
      const data = await res.json();
      if (data.ok) {
        setFeedbackSent(true);
        setShowFeedback(false);
        const nomeSalvo = newDishName;
        setNewDishName("");
        
        // Atualizar lista de pratos
        loadDishes();
        
        // Mostrar confirmação clara ao usuário
        alert(`✅ ${data.message}\n\n📝 Prato: ${nomeSalvo}\n💰 Créditos usados: 0`);
        
        // Mostrar resultado do novo prato se tiver dados
        if (data.dish_info) {
          setResult({
            ok: true,
            identified: true,
            dish: data.dish_slug,
            dish_display: data.dish_name,
            confidence: 'alta',
            score: 1.0,
            message: 'Prato cadastrado com sucesso!',
            ...data.dish_info,
            source: 'new_dish'
          });
        }
      } else {
        alert(data.error || 'Erro ao criar prato');
      }
    } catch (e) {
      console.error('Erro ao criar prato:', e);
      if (e.name === 'AbortError') {
        alert('Tempo limite excedido. Tente novamente.');
      } else {
        alert('Erro ao criar prato: ' + (e.message || 'Erro de conexão'));
      }
    } finally {
      if (mountedRef.current) {
        setCreatingDish(false);
      }
    }
  };

  // MELHORAR IDENTIFICAÇÃO COM IA (sob demanda - consome créditos)
  const melhorarComIA = async () => {
    if (!lastImageBlob) {
      alert('Nenhuma imagem para analisar');
      return;
    }
    
    // Confirmar com usuário
    const confirmar = window.confirm(
      '🤖 Usar IA para melhorar identificação?\n\n' +
      '⚠️ Isso consome créditos do sistema.\n\n' +
      'Continuar?'
    );
    
    if (!confirmar) return;
    
    setLoadingIA(true);
    
    const fd = new FormData();
    fd.append("file", lastImageBlob, "photo.jpg");
    fd.append("restaurant", getRestaurantValue());
    
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);
      
      const res = await fetch(`${API}/ai/identify-with-ai`, {
        method: "POST",
        body: fd,
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      
      const data = await res.json();
      
      if (data.ok && data.identified) {
        // Atualizar resultado com dados da IA
        setResult(prev => ({
          ...prev,
          ...data,
          dish_display: data.dish_display,
          category: data.category,
          category_emoji: data.category_emoji,
          confidence: data.confidence || 'alta',
          ingredientes: data.ingredientes,
          beneficios: data.beneficios,
          descricao: data.descricao,
          source: 'gemini_ai',
          ia_disponivel: false // Já usou IA
        }));
        
        alert(`✅ IA identificou: ${data.dish_display}\n\n💰 Créditos consumidos`);
      } else {
        alert(`❌ IA não conseguiu identificar\n\n${data.error || 'Tente corrigir manualmente'}`);
      }
    } catch (e) {
      console.error('Erro ao chamar IA:', e);
      if (e.name === 'AbortError') {
        alert('Tempo limite excedido. Tente novamente.');
      } else {
        alert('Erro ao chamar IA: ' + (e.message || 'Erro de conexão'));
      }
    } finally {
      setLoadingIA(false);
    }
  };

  // Descartar foto
  const discardPhoto = () => {
    setShowFeedback(false);
    setFeedbackSent(true);
    setNewDishName("");
  };

  // Função para fechar welcome e mostrar tutorial se for primeira vez
  const handleWelcomeClose = () => {
    localStorage.setItem('soulnutri_welcomed', 'true');
    setShowWelcome(false);
    // Mostrar tutorial se nunca viu
    if (!localStorage.getItem('soulnutri_tutorial_seen')) {
      setTimeout(() => setShowScannerTutorial(true), 300);
    }
  };

  const r = result;
  
  // Config de confiança
  const confidenceConfig = {
    alta: { color: "#10b981", label: "ALTA CONFIANÇA", bg: "rgba(16,185,129,0.15)" },
    média: { color: "#f59e0b", label: "MÉDIA CONFIANÇA", bg: "rgba(245,158,11,0.15)" },
    baixa: { color: "#ef4444", label: "BAIXA CONFIANÇA", bg: "rgba(239,68,68,0.15)" }
  };
  
  const confData = confidenceConfig[r?.confidence] || confidenceConfig.baixa;
  
  // Cores das categorias
  const getCategoryStyle = (cat) => {
    switch(cat) {
      case 'vegano': return { bg: '#22c55e', color: '#fff' };
      case 'vegetariano': return { bg: '#fff', color: '#333', border: '1px solid #333' };
      case 'proteína animal': return { bg: '#f97316', color: '#fff' };
      default: return { bg: '#666', color: '#fff' };
    }
  };
  
  const catStyle = getCategoryStyle(r?.category);

  // Formatar alertas de alérgenos - SEMPRE MOSTRAR
  const getAllergenDisplay = (riscos) => {
    if (!riscos || riscos.length === 0) {
      return { hasAllergens: false, text: "✅ Não contém alérgenos conhecidos" };
    }
    const riscosSafe = Array.isArray(riscos) ? riscos : [];
    const allergenRisks = riscosSafe.filter((r) => {
    const text = safeText(r);
  return (
    text.includes('alérgeno') ||
    text.includes('contém') ||
    text.includes('glúten') ||
    text.includes('lactose') ||
    text.includes('crustáceo') ||
    text.includes('camarão') ||
    text.includes('amendoim') ||
    text.includes('soja') ||
    text.includes('ovo')
  );
});    
    if (allergenRisks.length === 0) {
      return { hasAllergens: false, text: "✅ Não contém alérgenos conhecidos" };
    }
    
    // Remover duplicações - priorizar "contém" sobre "pode conter"
    const seen = new Set();
    const uniqueAlerts = allergenRisks.filter(risco => {
    const key = safeText(risco || '')
      .replace('pode conter traços de ', '')
        .replace('contém ', '')
        .replace('pode conter ', '')
        .trim();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    
    const safeLower = (v) =>
      typeof v === "string" ? v.toLowerCase() : "";

    return { 
      hasAllergens: true, 
      alerts: uniqueAlerts.map(risco => ({
        type: safeLower(risco).includes('pode conter') ? 'possible' : 'definite',
        text: typeof risco === 'object' ? (risco.texto || risco.text || '') : risco
      }))
    };
  };

  const allergenInfo = getAllergenDisplay(r?.riscos);

  // Filtrar pratos na busca
  const filteredDishes = dishes.filter(d => 
    d.name.toLowerCase().includes(searchFilter.toLowerCase())
  );

  // Função para instalar PWA
  const handleInstallApp = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setIsInstalled(true);
      }
      setDeferredPrompt(null);
    }
    setShowMenu(false);
  };

  return (
    <div
      className={`app${premiumUser ? ' premium-active' : ''}`}
      data-auto-update-test="v1"
    >
      {/* Banner de atualização disponível */}
      {hasUpdate && (
        <div
          data-testid="update-banner"
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, zIndex: 99999,
            background: 'linear-gradient(90deg, #f59e0b, #d97706)',
            color: '#1a1a1a', padding: '10px 16px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)', fontFamily: 'sans-serif',
          }}
        >
          <span style={{ fontSize: 14, fontWeight: 600 }}>
            Nova versão disponível
          </span>
          <button
            data-testid="update-app-btn"
            onClick={triggerHardUpdate}
            style={{
              background: '#1a1a1a', color: '#f59e0b',
              border: 'none', borderRadius: 20,
              padding: '6px 16px', fontSize: 13, fontWeight: 700,
              cursor: 'pointer', whiteSpace: 'nowrap',
            }}
          >
            Atualizar aplicativo
          </button>
        </div>
      )}
      {/* Tela de Permissões Unificada */}
      {showPermissions && (
        <div className="permissions-screen" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '20px'
        }}>
          <img src="/images/soulnutri-logo.png" alt="SoulNutri" style={{ width: '120px', marginBottom: '24px' }} />
          <h2 style={{ color: '#fff', fontSize: '24px', marginBottom: '16px', textAlign: 'center' }}>
            Bem-vindo ao SoulNutri
          </h2>
          <p style={{ color: '#aaa', textAlign: 'center', marginBottom: '32px', maxWidth: '300px' }}>
            Para identificar seus pratos, precisamos de acesso à câmera.
          </p>
          
          <div style={{ 
            background: 'rgba(255,255,255,0.1)', 
            borderRadius: '16px', 
            padding: '24px',
            width: '100%',
            maxWidth: '320px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px', color: '#fff' }}>
              <span style={{ fontSize: '28px', marginRight: '16px' }}>📷</span>
              <div>
                <div style={{ fontWeight: 'bold' }}>Câmera</div>
                <div style={{ fontSize: '13px', color: '#aaa' }}>Para fotografar os pratos</div>
              </div>
              {permissionsStatus.camera === 'granted' && <span style={{ marginLeft: 'auto', color: '#22c55e' }}>✓</span>}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', color: '#fff' }}>
              <span style={{ fontSize: '28px', marginRight: '16px' }}>📍</span>
              <div>
                <div style={{ fontWeight: 'bold' }}>Localização</div>
                <div style={{ fontSize: '13px', color: '#aaa' }}>Para personalizar o serviço</div>
              </div>
              {permissionsStatus.location === 'granted' && <span style={{ marginLeft: 'auto', color: '#22c55e' }}>✓</span>}
            </div>
          </div>
          
          <button
            onClick={requestAllPermissions}
            style={{
              marginTop: '32px',
              padding: '16px 48px',
              fontSize: '18px',
              fontWeight: 'bold',
              background: 'linear-gradient(135deg, #22c55e, #16a34a)',
              color: '#fff',
              border: 'none',
              borderRadius: '12px',
              cursor: 'pointer',
              boxShadow: '0 4px 20px rgba(34, 197, 94, 0.4)'
            }}
          >
            Permitir e Continuar
          </button>
          
          <button
            onClick={() => {
              localStorage.setItem('soulnutri_permissions_granted', 'true');
              setShowPermissions(false);
            }}
            style={{
              marginTop: '16px',
              padding: '12px 24px',
              fontSize: '14px',
              background: 'transparent',
              color: '#888',
              border: '1px solid #444',
              borderRadius: '8px',
              cursor: 'pointer'
            }}
          >
            Pular por agora
          </button>
        </div>
      )}

      {/* Modal de seleção manual de localização (quando GPS negado) */}
      {showLocationPrompt && (
        <div data-testid="location-prompt" style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.85)', display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', zIndex: 10001, padding: '20px'
        }}>
          <div style={{
            background: '#1a1a2e', borderRadius: '20px', padding: '32px',
            maxWidth: '340px', width: '100%', textAlign: 'center'
          }}>
            <span style={{ fontSize: '48px', display: 'block', marginBottom: '16px' }}>📍</span>
            <h3 style={{ color: '#fff', fontSize: '20px', marginBottom: '8px' }}>
              Onde você está?
            </h3>
            <p style={{ color: '#aaa', fontSize: '14px', marginBottom: '24px' }}>
              Selecione sua localização para uma melhor experiência:
            </p>
            <button
              data-testid="location-cibi-sana"
              onClick={() => selectLocationManual('cibi_sana')}
              style={{
                width: '100%', padding: '14px', fontSize: '16px', fontWeight: 'bold',
                background: 'linear-gradient(135deg, #d4af37, #b8960f)', color: '#080808',
                border: 'none', borderRadius: '12px', cursor: 'pointer', marginBottom: '12px'
              }}
            >
              Estou no Cibi Sana
            </button>
            <button
              data-testid="location-external"
              onClick={() => selectLocationManual('external')}
              style={{
                width: '100%', padding: '14px', fontSize: '16px', fontWeight: '600',
                background: 'linear-gradient(135deg, #3b82f6, #2563eb)', color: '#fff',
                border: 'none', borderRadius: '12px', cursor: 'pointer'
              }}
            >
              Estou em outro local
            </button>
          </div>
        </div>
      )}

      {/* Header com Logo e Menu */}
      <header className="hdr">
        <div className="logo-container">
          <img src="/images/soulnutri-logo.png" alt="SoulNutri" className="logo" />
          <span className="trademark">®</span>
          {premiumUser && (
            <span className="premium-badge-header" data-testid="premium-badge">PREMIUM</span>
          )}
        </div>
        <div className="header-right">
          <LanguageSelector />
          {detectedRestaurant ? (
            <span
              data-testid="location-indicator"
              onClick={() => {
                // Parar GPS para não fechar o modal automaticamente
                if (watchIdRef.current !== null) {
                  navigator.geolocation.clearWatch(watchIdRef.current);
                  watchIdRef.current = null;
                }
                localStorage.removeItem('soulnutri_location_manual');
                setShowLocationPrompt(true);
              }}
              style={{
                fontSize: '11px',
                padding: '3px 8px',
                borderRadius: '10px',
                cursor: 'pointer',
                fontWeight: '600',
                background: detectedRestaurant === 'cibi_sana' 
                  ? 'rgba(212,175,55,0.2)' : 'rgba(59,130,246,0.2)',
                color: detectedRestaurant === 'cibi_sana' ? '#d4af37' : '#60a5fa',
                border: `1px solid ${detectedRestaurant === 'cibi_sana' ? '#d4af3750' : '#3b82f650'}`
              }}
            >
              {detectedRestaurant === 'cibi_sana' ? '📍 Cibi Sana' : '🌐 Externo'}
            </span>
          ) : (
            <span
              data-testid="location-indicator-pending"
              onClick={() => setShowLocationPrompt(true)}
              style={{
                fontSize: '11px',
                padding: '3px 8px',
                borderRadius: '10px',
                cursor: 'pointer',
                fontWeight: '600',
                background: 'rgba(245,158,11,0.2)',
                color: '#f59e0b',
                border: '1px solid rgba(245,158,11,0.3)'
              }}
            >
              📍 Selecionar local
            </span>
          )}
          {status?.ready && <span className="st">✓ {status.total_dishes} pratos</span>}
          {premiumUser && (
            <button
              className="notif-btn"
              onClick={() => { setShowNotifications(!showNotifications); setShowMenu(false); }}
              data-testid="notification-bell"
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                position: 'relative',
                padding: '6px',
                fontSize: '18px'
              }}
            >
              🔔
              {notifUnread > 0 && (
                <span style={{
                  position: 'absolute',
                  top: '0',
                  right: '0',
                  background: '#d4af37',
                  color: '#080808',
                  borderRadius: '50%',
                  width: '16px',
                  height: '16px',
                  fontSize: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold'
                }}>{notifUnread}</span>
              )}
            </button>
          )}
          <button 
            className="menu-btn" 
            onClick={() => setShowMenu(!showMenu)}
            data-testid="menu-button"
          >
            ☰
          </button>
        </div>
      </header>

      {/* Toast de confirmação - Refeição registrada */}
      {mealRegistered && (
        <div 
          className="meal-registered-toast"
          data-testid="meal-registered-toast"
          style={{
            position: 'fixed',
            top: '70px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'linear-gradient(135deg, #22c55e, #16a34a)',
            color: 'white',
            padding: '16px 24px',
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(34, 197, 94, 0.4)',
            zIndex: 9999,
            textAlign: 'center',
            animation: 'slideDown 0.3s ease-out'
          }}
        >
          <div style={{ fontSize: '24px', marginBottom: '4px' }}>✅</div>
          <div style={{ fontWeight: 'bold', fontSize: '16px' }}>Registrado com sucesso!</div>
          <div style={{ fontSize: '13px', opacity: 0.9, marginTop: '4px' }}>
            🍽️ Aproveite sua refeição!
          </div>
        </div>
      )}

      {shouldShowIOSInstallHelp && (
        <div
          style={{
            position: 'fixed',
            top: '12px',
            left: '12px',
            right: '12px',
            zIndex: 9998,
            background: 'rgba(17, 24, 39, 0.96)',
            color: '#fff',
            borderRadius: '14px',
            padding: '14px 16px',
            boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
            border: '1px solid rgba(255,255,255,0.12)'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
            <div style={{ fontWeight: 700, fontSize: '15px' }}>📲 Instale o SoulNutri no iPhone</div>
            <button onClick={() => { setInstallBannerDismissed(true); localStorage.setItem('soulnutri_install_dismissed', '1'); }}
              style={{ background: 'none', border: 'none', color: '#aaa', fontSize: '20px', cursor: 'pointer', lineHeight: 1, padding: '0 4px' }}>✕</button>
          </div>
          <div style={{ fontSize: '13px', lineHeight: 1.45, opacity: 0.95 }}>
            Toque em <strong>Compartilhar</strong> no Safari e depois em <strong>Adicionar à Tela de Início</strong>.
          </div>
        </div>
      )}

      {shouldShowAndroidInstallHelp && (
        <div
          style={{
            position: 'fixed',
            top: '12px',
            left: '12px',
            right: '12px',
            zIndex: 9998,
            background: 'rgba(17, 24, 39, 0.96)',
            color: '#fff',
            borderRadius: '14px',
            padding: '14px 16px',
            boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
            border: '1px solid rgba(255,255,255,0.12)'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
            <div style={{ fontWeight: 700, fontSize: '15px' }}>📲 Instale o app SoulNutri</div>
            <button onClick={() => { setInstallBannerDismissed(true); localStorage.setItem('soulnutri_install_dismissed', '1'); }}
              style={{ background: 'none', border: 'none', color: '#aaa', fontSize: '20px', cursor: 'pointer', lineHeight: 1, padding: '0 4px' }}>✕</button>
          </div>
          {deferredPrompt ? (
            <button
              onClick={handleInstallApp}
              style={{
                marginTop: '8px',
                background: '#10b981',
                color: '#fff',
                border: 'none',
                borderRadius: '10px',
                padding: '10px 14px',
                fontWeight: 700,
                cursor: 'pointer'
              }}
            >
              Instalar SoulNutri
            </button>
          ) : (
            <div style={{ fontSize: '13px', lineHeight: 1.45, opacity: 0.95 }}>
              No Chrome, toque nos <strong>3 pontos</strong> e depois em <strong>Adicionar à tela inicial</strong> ou <strong>Instalar app</strong>.
            </div>
          )}
        </div>
      )}

      {/* Menu dropdown */}
      {showMenu && (
        <div className="menu-dropdown" data-testid="menu-dropdown">
          {!isInstalled && deferredPrompt && (
            <button className="menu-item install" onClick={handleInstallApp}>
              📲 Adicionar à tela inicial
            </button>
          )}
          {isInstalled && (
            <div className="menu-item installed">
              ✅ App instalado
            </div>
          )}
          {!deferredPrompt && !isInstalled && (
            <div className="menu-item info">
              📱 Para instalar: use o menu do navegador → &quot;Adicionar à tela inicial&quot;
            </div>
          )}
          <button className="menu-item" onClick={() => { setShowMenu(false); setShowScannerTutorial(true); }} data-testid="menu-tutorial">
            📸 {t('how_scanner_works', 'Como usar o SoulNutri')}
          </button>
          <button className="menu-item" onClick={() => { setShowMenu(false); setShowNutriFeed(true); }} data-testid="menu-nutri-feed">
            📰 Nutri News
          </button>
          <button className="menu-item" onClick={() => { setShowMenu(false); setShowWeeklyReport(true); }} data-testid="menu-weekly-report">
            📊 Relatorio Semanal
          </button>
          <button className="menu-item" onClick={() => { setShowMenu(false); checkStatus(); loadDishes(); }}>
            🔄 Atualizar pratos
          </button>
          <button className="menu-item" onClick={() => setShowMenu(false)}>
            ✕ Fechar
          </button>
        </div>
      )}

      {/* Câmera com moldura guia */}
      <div 
        className="cam-box" 
        onClick={cameraError ? startCamera : handleCameraTouch}
        data-testid="camera-container"
      >
        <video ref={videoRef} autoPlay playsInline muted />
        <canvas ref={canvasRef} hidden />
        
        {/* Erro de câmera */}
        {cameraError && (
          <div className="cam-error" data-testid="camera-error">
            <span>📷</span>
            <p>{cameraError === 'permission_denied' 
              ? t('permission_denied', 'Permissão negada. Toque para permitir.') 
              : t('camera_error', 'Câmera não disponível')}</p>
            <button onClick={(e) => { e.stopPropagation(); startCamera(); }}>
              🔄 {t('try_again', 'Tentar novamente')}
            </button>
          </div>
        )}
        
        {!cameraError && (
          <div className="cam-guide">
            <div className="guide-frame"></div>
            <span className="guide-text">{t('position_dish', 'Posicione o prato aqui')}</span>
          </div>
        )}
        
        {loading && (
          <div className="cam-loading">
            <span>🔍</span>
            <p>{t('loading', 'Identificando...')}</p>
          </div>
        )}
        
        {!loading && !r && !cameraError && (
          <div className="cam-hint">
            <span>👆</span>
            <p>Toque para fotografar</p>
          </div>
        )}

        {/* BOTÃO VOLTAR À REFEIÇÃO - quando há itens salvos e está na câmera */}
        {!loading && !r && plateItems.length > 0 && viewMode !== 'mesa' && (
          <div data-testid="voltar-refeicao-btn" style={{
            position: 'absolute', bottom: '20px', left: '50%', transform: 'translateX(-50%)',
            zIndex: 50, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px'
          }}>
            <button
              onClick={() => setViewMode('mesa')}
              style={{
                background: 'linear-gradient(135deg, #d4af37, #b8960f)',
                color: '#080808', border: 'none', borderRadius: '25px',
                padding: '12px 24px', fontSize: '14px', fontWeight: 'bold',
                cursor: 'pointer', boxShadow: '0 4px 15px rgba(212,175,55,0.3)',
                display: 'flex', alignItems: 'center', gap: '8px',
                animation: 'pulse 2s ease-in-out infinite'
              }}
            >
              🍽️ Voltar à minha refeição ({plateItems.length} {plateItems.length === 1 ? 'item' : 'itens'})
            </button>
          </div>
        )}

        {/* SCANNER OVERLAY - Mostra info em tempo real */}
        {scannerResult && !loading && !r && (
          <div 
            className="scanner-overlay" 
            onClick={handleScannerTap}
            data-testid="scanner-overlay"
          >
            <div className="scanner-result">
              <div className="scanner-header">
                <span className="scanner-icon">✅</span>
                <span className="scanner-dish">{scannerResult.dish_display}</span>
              </div>
              <div className="scanner-info">
                <span className="scanner-cal">{scannerResult.calorias}</span>
                <span className="scanner-cat">{scannerResult.categoria}</span>
              </div>
              {scannerResult.contem_gluten === false && (
                <span className="scanner-badge gluten-free">Sem gluten</span>
              )}
              {scannerResult.riscos?.length > 0 && (
                <span className="scanner-badge warning">{renderTextSafe(scannerResult.riscos[0])}</span>
              )}
              {/* ALERTAS PREMIUM - destaque visual para decidir */}
              {scannerResult.premium?.alertas_alergenos?.length > 0 && (
                <div data-testid="scanner-allergen-alerts" style={{
                  margin: '8px 0', padding: '8px 12px',
                  background: 'rgba(239,68,68,0.25)', border: '2px solid rgba(239,68,68,0.6)',
                  borderRadius: '10px', animation: 'pulse 1.5s infinite'
                }}>
                  {scannerResult.premium.alertas_alergenos.map((al, i) => (
                    <div key={i} style={{color:'#fca5a5',fontWeight:'bold',fontSize:'14px',padding:'2px 0'}}>
                      {renderTextSafe(al.icone)} {renderTextSafe(al.mensagem)}
                    </div>
                  ))}
                </div>
              )}
              {/* Alergenos detectados */}
              {scannerResult.alergenos && Object.entries(scannerResult.alergenos).filter(([,v])=>v).length > 0 && !scannerResult.premium?.alertas_alergenos?.length && (
                <div style={{margin:'4px 0',display:'flex',gap:'4px',flexWrap:'wrap'}}>
                  {Object.entries(scannerResult.alergenos).filter(([,v])=>v).map(([k]) => (
                    <span key={k} className="scanner-badge warning" style={{fontSize:'11px'}}>{k}</span>
                  ))}
                </div>
              )}
              <p className="scanner-tap">Toque para mais detalhes</p>
            </div>
          </div>
        )}
      </div>

      {/* Botões de ação */}
      <div className="action-btns">
        <button 
          className="action-btn upload" 
          onClick={() => fileInputRef.current?.click()}
          data-testid="upload-button"
          style={{ background: '#4a9eff' }}
        >
          📁 Foto
        </button>
        <button 
          className="action-btn gallery" 
          onClick={() => setShowGalleryView(true)}
          data-testid="gallery-button"
        >
          🖼️ {t('gallery', 'Galeria')} {photoGallery.length > 0 && `(${photoGallery.length})`}
        </button>
        <button 
          className="action-btn clear" 
          onClick={() => { clearResult(); clearPlate(); setViewMode('buffet'); startCamera(); }}
          disabled={!r && !multiResult && !error && plateItems.length === 0}
          data-testid="clear-button"
        >
          🗑️ Limpar
        </button>
      </div>

      {/* ══════════════════════════════════════════════════════════
          MODAL GALERIA DE FOTOS
          ══════════════════════════════════════════════════════════ */}
      {showGalleryView && (
        <div className="gallery-modal-overlay" onClick={() => setShowGalleryView(false)}>
          <div className="gallery-modal" onClick={e => e.stopPropagation()}>
            <div className="gallery-header">
              <h2>🖼️ Minhas Fotos</h2>
              <button className="close-btn" onClick={() => setShowGalleryView(false)}>✕</button>
            </div>
            
            {photoGallery.length === 0 ? (
              <div className="gallery-empty">
                <p>📷 Nenhuma foto ainda</p>
                <p>Suas fotos de pratos aparecerão aqui</p>
              </div>
            ) : (
              <>
                <div className="gallery-grid">
                  {photoGallery.map(photo => (
                    <div key={photo.id} className="gallery-item">
                      <img src={photo.imageUrl} alt={photo.dishName} />
                      <div className="gallery-item-info">
                        <span className="gallery-item-name">{photo.dishName}</span>
                        <span className="gallery-item-date">{photo.date}</span>
                        {photo.result?.calorias && (
                          <span className="gallery-item-cal">{photo.result.calorias}</span>
                        )}
                      </div>
                      <button 
                        className="gallery-delete-btn"
                        onClick={() => deleteFromGallery(photo.id)}
                      >
                        🗑️
                      </button>
                    </div>
                  ))}
                </div>
                <button className="gallery-clear-btn" onClick={clearGallery}>
                  Limpar todas as fotos
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════
          RESUMO DO PRATO - VISTA MESA (informações CONSOLIDADAS)
          ══════════════════════════════════════════════════════════ */}
      {plateItems.length > 0 && viewMode === 'mesa' && (
        <div className="plate-mesa-view" data-testid="plate-mesa">
          {/* BOTÃO VOLTAR */}
          <button 
            className="back-btn"
            onClick={() => { setViewMode('buffet'); startCamera(); }}
            data-testid="mesa-back-btn"
          >
            ← Voltar ao buffet
          </button>
          
          <h2 className="mesa-title">🍽️ Seu Prato Completo</h2>
          <p className="mesa-subtitle">{plateItems.length} itens selecionados</p>
          
          {/* BOTAO OUVIR - TTS Premium Completo (benefícios, riscos, curiosidades, etc.) */}
          {premiumUser && plateConsolidated && (
            <button
              className="tts-btn"
              data-testid="mesa-tts-btn"
              onClick={() => playDishAudio({
                dish_display: plateConsolidated.itens?.join(' e ') || 'Seu Prato',
                nutrition: plateConsolidated.nutrition,
                ingredientes: plateConsolidated.ingredientes,
                beneficios: plateConsolidated.beneficios,
                riscos: plateConsolidated.riscos,
                alergenos: {
                  gluten: plateConsolidated.contemGluten,
                  lactose: plateConsolidated.contemLactose,
                  ovo: plateConsolidated.contemOvo,
                  castanhas: plateConsolidated.contemCastanhas,
                  frutosMar: plateConsolidated.contemFrutosMar,
                  soja: plateConsolidated.contemSoja,
                },
                curiosidade: plateConsolidated.curiosidades?.join('. ') || '',
                combinacoes: plateConsolidated.combinacoes,
                noticias: plateConsolidated.noticias,
                beneficio_principal: plateConsolidated.beneficio_principal?.join('. ') || '',
                alerta_saude: plateConsolidated.alerta_saude?.join('. ') || '',
                mito_verdade: plateConsolidated.mitos_verdades?.[0] || null,
              }, true)}
              disabled={ttsLoading || enrichLoading}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                gap: '10px', width: '100%', padding: '14px 20px', margin: '8px 0 12px',
                background: ttsPlaying
                  ? 'linear-gradient(135deg, #ef4444, #dc2626)'
                  : enrichLoading
                    ? 'linear-gradient(135deg, #6b7280, #4b5563)'
                    : 'linear-gradient(135deg, #ffd700, #ff8c00)',
                color: enrichLoading ? '#ccc' : '#000', border: 'none', borderRadius: '14px',
                fontSize: '16px', fontWeight: '700',
                cursor: (ttsLoading || enrichLoading) ? 'wait' : 'pointer',
                boxShadow: '0 4px 12px rgba(255,215,0,0.3)', minHeight: '52px',
              }}
            >
              {ttsLoading ? (
                <span>Gerando audio premium...</span>
              ) : enrichLoading ? (
                <span>Aguardando dados premium...</span>
              ) : ttsPlaying ? (
                <><span style={{fontSize:'20px'}}>&#9724;</span> Parar</>
              ) : (
                <><span style={{fontSize:'20px'}}>&#128266;</span> Ouvir resumo completo</>
              )}
            </button>
          )}
          
          {/* Lista dos itens escolhidos */}
          <div className="mesa-items-list">
            {plateItems.map((item, i) => (
              <span key={item.id} className="mesa-item-tag" style={{display:'flex',alignItems:'center',gap:'6px'}}>
                {item.dish_display}
                <span
                  data-testid={`remove-plate-item-${i}`}
                  onClick={() => setPlateItems(prev => prev.filter(p => p.id !== item.id))}
                  style={{cursor:'pointer',color:'#ef4444',fontWeight:'bold',fontSize:'16px',lineHeight:'1',padding:'0 4px'}}
                  title="Remover do prato"
                >x</span>
              </span>
            ))}
          </div>

          {/* ALÉRGENOS CONSOLIDADOS */}
          <div className="mesa-allergens">
            <h4>⚠️ Alérgenos no seu prato</h4>
            {plateConsolidated?.contemGluten && <span className="allergen-tag warning">🌾 Glúten</span>}
            {plateConsolidated?.contemLactose && <span className="allergen-tag warning">🥛 Lactose</span>}
            {plateConsolidated?.contemOvo && <span className="allergen-tag warning">🥚 Ovo</span>}
            {plateConsolidated?.contemCastanhas && <span className="allergen-tag warning">🥜 Castanhas</span>}
            {plateConsolidated?.contemFrutosMar && <span className="allergen-tag warning">🦐 Frutos do Mar</span>}
            {plateConsolidated?.contemSoja && <span className="allergen-tag warning">🫘 Soja</span>}
            {!plateConsolidated?.contemGluten && !plateConsolidated?.contemLactose && 
             !plateConsolidated?.contemOvo && !plateConsolidated?.contemCastanhas && 
             !plateConsolidated?.contemFrutosMar && !plateConsolidated?.contemSoja && (
              <span className="allergen-tag neutral">✅ Nenhum alérgeno comum detectado</span>
            )}
          </div>

          {/* DETALHES POR ITEM */}
          <div className="mesa-section">
            <h4>📋 Detalhes por item</h4>
            <div className="mesa-items-details">
              {plateItems.map((item, i) => (
                <div key={item.id} className="mesa-item-detail">
                  <span className="item-name">{item.dish_display}</span>
                  <span className="item-cal">{item.calorias}</span>
                  <span className="item-cat">{item.category || 'N/A'}</span>
                </div>
              ))}
            </div>
          </div>

          {/* FICHA NUTRICIONAL CONSOLIDADA */}
          <div className="mesa-nutrition">
            <h4>Ficha Nutricional (por 100g)</h4>
            <div className="nutr-grid">
              <div><b>{plateConsolidated?.nutrition?.calorias}</b><small>Calorias</small></div>
              <div><b>{plateConsolidated?.nutrition?.proteinas}</b><small>Proteinas</small></div>
              <div><b>{plateConsolidated?.nutrition?.carboidratos}</b><small>Carbos</small></div>
              <div><b>{plateConsolidated?.nutrition?.gorduras}</b><small>Gorduras</small></div>
            </div>
            {/* Micronutrientes - apenas se tiver dados precisos da ficha */}
            {plateConsolidated?.nutrition?.fibras_g != null && (
              <div className="nutr-grid" style={{marginTop: '8px', opacity: 0.85}}>
                <div><b>{plateConsolidated.nutrition.fibras_g?.toFixed(1)}g</b><small>Fibras</small></div>
                <div><b>{plateConsolidated.nutrition.calcio_mg?.toFixed(0) || '-'}mg</b><small>Calcio</small></div>
                <div><b>{plateConsolidated.nutrition.ferro_mg?.toFixed(1) || '-'}mg</b><small>Ferro</small></div>
                <div><b>{plateConsolidated.nutrition.sodio_mg?.toFixed(0) || '-'}mg</b><small>Sodio</small></div>
              </div>
            )}
            {plateConsolidated?.nutrition?.fonte && (
              <div style={{fontSize: '11px', color: '#94a3b8', marginTop: '6px', textAlign: 'right'}}>
                Fonte: {renderTextSafe(plateConsolidated.nutrition.fonte)}
              </div>
            )}
          </div>

          {/* INGREDIENTES CONSOLIDADOS */}
          {plateConsolidated?.ingredientes?.length > 0 && (
            <div className="mesa-section">
              <h4>🥗 Ingredientes do seu prato</h4>
              <p>{plateConsolidated.ingredientes.join(', ')}</p>
            </div>
          )}

          {/* BENEFÍCIOS CONSOLIDADOS */}
          {plateConsolidated?.beneficios?.length > 0 && (
            <div className="mesa-section good">
              <h4>✅ Benefícios da sua refeição</h4>
<ul>{plateConsolidated.beneficios.map((b,i) => <li key={i}>{typeof b === 'object' ? (<>{b.texto}{b.fonte && (
  <a 
    href={`https://www.google.com/search?q=${encodeURIComponent(b.fonte + ' nutrição estudo')}`}
    target="_blank"
    rel="noopener noreferrer"
    style={{color:'#10b981',fontSize:'11px',fontStyle:'italic'}}
  >
    {" — " + b.fonte}
  </a>
)}</>) : b}</li>)}</ul>
            </div>
          )}

          {/* ══════════════════════════════════════════════════════════
              CONTEÚDO PREMIUM - Radar de Notícias (apenas na vista completa)
              ══════════════════════════════════════════════════════════ */}
          
          {/* LOADING PREMIUM */}
          {premiumUser && enrichLoading && (
            <div data-testid="enrich-loading" style={{
              background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.1), rgba(255, 165, 0, 0.05))',
              border: '1px solid rgba(255, 215, 0, 0.2)',
              borderRadius: '12px',
              padding: '16px',
              textAlign: 'center',
              margin: '12px 0',
              animation: 'pulse 1.5s ease-in-out infinite'
            }}>
              <span style={{ color: '#ffd700', fontSize: '14px' }}>
                Carregando conteudo Premium...
              </span>
            </div>
          )}

          {/* AVISO PARA USUÁRIOS FREE */}
          {!premiumUser && (
            <div className="premium-upsell" style={{
              background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.1), rgba(255, 165, 0, 0.1))',
              border: '1px solid rgba(255, 215, 0, 0.3)',
              borderRadius: '12px',
              padding: '16px',
              textAlign: 'center',
              margin: '12px 0'
            }}>
              <h4 style={{ color: '#ffd700', marginBottom: '8px' }}>⭐ Versão Premium</h4>
              <p style={{ color: '#ccc', fontSize: '13px', margin: '0 0 12px' }}>
                Desbloqueie: Curiosidades científicas, Combinações inteligentes, Alertas personalizados e mais!
              </p>
              <button 
                onClick={() => setShowPremium('login')}
                style={{
                  background: 'linear-gradient(135deg, #ffd700, #ff8c00)',
                  color: '#000',
                  border: 'none',
                  borderRadius: '20px',
                  padding: '8px 24px',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                Conhecer Premium
              </button>
            </div>
          )}
          
          {/* CONTEÚDO EXCLUSIVO PREMIUM */}
          {premiumUser && (
            <>
              {/* CURIOSIDADES CIENTÍFICAS */}
              {(plateConsolidated?.curiosidades?.length > 0 || radarInfo?.voce_sabia) && (
                <div className="mesa-section voce-sabia" data-testid="mesa-curiosidades" style={{
                  background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15), rgba(139, 92, 246, 0.15))',
                  borderLeft: '3px solid #3b82f6',
                  borderRadius: '8px',
                  padding: '12px'
                }}>
                  <h4 style={{ color: '#60a5fa', marginBottom: '8px' }}>💡 Curiosidades</h4>
                  {plateConsolidated.curiosidades.map((c, i) => (
                    <p key={i} style={{ color: '#d1d5db', fontSize: '13px', lineHeight: '1.5', margin: '0 0 6px' }}>
                      {renderTextSafe(c)}
                    </p>
                  ))}
                  {radarInfo?.voce_sabia && !plateConsolidated.curiosidades.some(c => c === radarInfo.voce_sabia) && (
                    <p style={{ color: '#d1d5db', fontSize: '13px', lineHeight: '1.5', margin: 0 }}>
                      {renderTextSafe(radarInfo.voce_sabia)}
                    </p>
                  )}
                </div>
              )}

              {/* COMBINAÇÕES QUE POTENCIALIZAM */}
              {(plateConsolidated?.combinacoes?.length > 0 || radarInfo?.combinacoes?.length > 0) && (
                <div className="mesa-section combinacoes" data-testid="mesa-combinacoes" style={{
                  background: 'rgba(34, 197, 94, 0.1)',
                  borderLeft: '3px solid #22c55e',
                  borderRadius: '8px',
                  padding: '12px'
                }}>
                  <h4 style={{ color: '#22c55e', marginBottom: '8px' }}>🔗 Combinações que potencializam</h4>
                  {(plateConsolidated.combinacoes.length > 0 ? plateConsolidated.combinacoes : radarInfo?.combinacoes || []).map((combo, i) => (
                    <p key={i} style={{ color: '#ccc', fontSize: '12px', margin: '4px 0' }}>• {renderTextSafe(combo)}</p>
                  ))}
                </div>
              )}

              {/* NOTICIAS E ALERTAS SOBRE INGREDIENTES */}
              {plateConsolidated?.noticias?.length > 0 && (
                <div className="mesa-section" data-testid="mesa-noticias" style={{
                  background: 'rgba(251, 191, 36, 0.1)',
                  borderLeft: '3px solid #fbbf24',
                  borderRadius: '8px',
                  padding: '12px'
                }}>
                  <h4 style={{ color: '#fbbf24', marginBottom: '8px' }}>Noticias e Alertas sobre Ingredientes</h4>
                  {plateConsolidated.noticias.map((noticia, i) => {
                    const texto = typeof noticia === 'string' ? noticia : (noticia?.texto || noticia?.titulo || '');
                    const fonte = typeof noticia === 'object' ? (noticia?.fonte || noticia?.url || noticia?.link) : null;
                    return (
                      <div key={i} style={{ color: '#d1d5db', fontSize: '13px', lineHeight: '1.5', margin: '0 0 8px', paddingLeft: '8px', borderLeft: '2px solid rgba(251, 191, 36, 0.3)' }}>
                        <p style={{margin:0}}>{renderTextSafe(texto)}</p>
                        {fonte && (
                          <span style={{color:'#fbbf24',fontSize:'11px',fontStyle:'italic',marginTop:'4px',display:'block'}}>
                            Fonte: {renderTextSafe(fonte)}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* BENEFÍCIO PRINCIPAL DETALHADO */}
              {plateConsolidated?.beneficio_principal?.length > 0 && (
                <div className="mesa-section" data-testid="mesa-beneficio-principal" style={{
                  background: 'rgba(16, 185, 129, 0.1)',
                  borderLeft: '3px solid #10b981',
                  borderRadius: '8px',
                  padding: '12px'
                }}>
                  <h4 style={{ color: '#34d399', marginBottom: '8px' }}>🌟 Benefício Principal</h4>
                  {plateConsolidated.beneficio_principal.map((b, i) => (
                    <p key={i} style={{ color: '#d1d5db', fontSize: '13px', lineHeight: '1.5', margin: '0 0 4px' }}>{renderTextSafe(b)}</p>
                  ))}
                </div>
              )}

              {/* ALERTA DE SAÚDE */}
              {plateConsolidated?.alerta_saude?.length > 0 && (
                <div className="mesa-section" data-testid="mesa-alerta-saude" style={{
                  background: 'rgba(245, 158, 11, 0.1)',
                  borderLeft: '3px solid #f59e0b',
                  borderRadius: '8px',
                  padding: '12px'
                }}>
                  <h4 style={{ color: '#fbbf24', marginBottom: '8px' }}>⚠️ Alertas de Saúde</h4>
                  {plateConsolidated.alerta_saude.map((a, i) => (
                    <p key={i} style={{ color: '#d1d5db', fontSize: '13px', lineHeight: '1.5', margin: '0 0 4px' }}>{renderTextSafe(a)}</p>
                  ))}
                </div>
              )}

              {/* VERDADE OU MITO */}
              {plateConsolidated?.mitos_verdades?.length > 0 && (
                <div className="mesa-section" data-testid="mesa-mito-verdade" style={{
                  background: 'rgba(168, 85, 247, 0.1)',
                  borderLeft: '3px solid #a855f7',
                  borderRadius: '8px',
                  padding: '12px'
                }}>
                  <h4 style={{ color: '#c084fc', marginBottom: '8px' }}>🧪 Verdade ou Mito?</h4>
                  {plateConsolidated.mitos_verdades.map((mv, i) => (
                    <div key={i} style={{ marginBottom: '8px' }}>
                      {(mv.mito || mv.afirmacao) && (
                        <p style={{ color: '#fff', fontSize: '13px', fontWeight: 'bold', margin: '0 0 4px' }}>
                          "{renderTextSafe(mv.mito || mv.afirmacao)}"
                        </p>
                      )}
                      {(mv.verdade || mv.resposta) && (
                        <p style={{ color: '#d1d5db', fontSize: '12px', margin: '0 0 4px' }}>
                          {renderTextSafe(mv.verdade || mv.resposta)}
                        </p>
                      )}
                      {mv.explicacao && <p style={{ color: '#9ca3af', fontSize: '11px', margin: 0, fontStyle: 'italic' }}>{renderTextSafe(mv.explicacao)}</p>}
                      {mv.fonte && <span style={{color:'#a855f7',fontSize:'11px',fontStyle:'italic',display:'block',marginTop:'4px'}}>Fonte: {renderTextSafe(mv.fonte)}</span>}
                    </div>
                  ))}
                </div>
              )}

              {/* DICA DO CHEF */}
              {plateConsolidated?.dicas_chef?.length > 0 && (
                <div className="mesa-section" data-testid="mesa-dica-chef" style={{
                  background: 'rgba(251, 146, 60, 0.1)',
                  borderLeft: '3px solid #fb923c',
                  borderRadius: '8px',
                  padding: '12px'
                }}>
                  <h4 style={{ color: '#fb923c', marginBottom: '8px' }}>👨‍🍳 Dica do Chef</h4>
                  {plateConsolidated.dicas_chef.map((d, i) => (
                    <p key={i} style={{ color: '#d1d5db', fontSize: '13px', lineHeight: '1.5', margin: '0 0 4px' }}>{renderTextSafe(d)}</p>
                  ))}
                </div>
              )}

              {/* ALERTAS PERSONALIZADOS */}
              {plateConsolidated?.alertas_personalizados?.length > 0 && (
                <div className="mesa-section" data-testid="mesa-alertas-personalizados" style={{
                  background: 'rgba(239, 68, 68, 0.1)',
                  borderLeft: '3px solid #ef4444',
                  borderRadius: '8px',
                  padding: '12px'
                }}>
                  <h4 style={{ color: '#f87171', marginBottom: '8px' }}>🔔 Alertas Personalizados</h4>
                  {plateConsolidated.alertas_personalizados.map((a, i) => (
                    <p key={i} style={{ color: '#d1d5db', fontSize: '13px', margin: '0 0 4px' }}>
                      {typeof a === 'string' ? a : `${a.icone || '⚠️'} ${renderTextSafe(a.mensagem || a.texto || a)}`}
                    </p>
                  ))}
                </div>
              )}

              {/* FATOS INTERESSANTES DO RADAR */}
              {radarInfo?.has_alert && radarInfo?.facts?.length > 0 && (
                <div className="mesa-section fatos" data-testid="mesa-fatos" style={{
                  background: radarInfo.type === 'alerta' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(245, 158, 11, 0.1)',
                  borderLeft: `3px solid ${radarInfo.type === 'alerta' ? '#ef4444' : '#f59e0b'}`,
                  borderRadius: '8px',
                  padding: '12px'
                }}>
                  <h4 style={{ 
                    color: radarInfo.type === 'alerta' ? '#f87171' : '#fbbf24', 
                    marginBottom: '8px' 
                  }}>
                    {radarInfo.type === 'alerta' ? '⚠️ Alerta Nutricional' : '📰 Fatos Interessantes'}
                  </h4>
                  {radarInfo.facts.slice(0, 2).map((fato, i) => (
                    <div key={i} style={{ marginBottom: '8px' }}>
                      {fato.fatos?.slice(0, 1).map((f, j) => (
                        <div key={j}>
                          <p style={{ color: '#fff', fontSize: '13px', fontWeight: 'bold', margin: '0 0 4px' }}>
                            {renderTextSafe(fato.emoji)} {renderTextSafe(f.titulo)}
                          </p>
                          <p style={{ color: '#ccc', fontSize: '12px', margin: '0 0 4px', lineHeight: '1.4' }}>
                            {renderTextSafe(f.resumo)}
                          </p>
                          <small style={{ color: '#888', fontSize: '10px' }}>Fonte: {renderTextSafe(f.fonte)}</small>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}

              {/* VOCÊ SABIA (radar) - só exibir se não houver curiosidades consolidadas */}
              {radarInfo?.voce_sabia && !plateConsolidated?.curiosidades?.length && (
                <div className="mesa-section voce-sabia" style={{
                  background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15), rgba(139, 92, 246, 0.15))',
                  borderLeft: '3px solid #3b82f6',
                  borderRadius: '8px',
                  padding: '12px'
                }}>
                  <h4 style={{ color: '#60a5fa', marginBottom: '8px' }}>💡 Você sabia?</h4>
                  <p style={{ color: '#d1d5db', fontSize: '13px', lineHeight: '1.5', margin: 0 }}>
                    {renderTextSafe(radarInfo.voce_sabia)}
                  </p>
                </div>
              )}
            </>
          )}

     {/* RISCOS CONSOLIDADOS */}
{plateConsolidated?.riscos?.length > 0 && (
  <div className="mesa-section warning">
    <h4>⚠️ Pontos de atenção</h4>
    <ul>
      {plateConsolidated.riscos.map((r,i) => (
        <li key={i}>
          {typeof r === 'object' ? (
            <>
              {renderTextSafe(r.texto)}
              {r.fonte && (
                <a 
                  href={`https://www.google.com/search?q=${encodeURIComponent(renderTextSafe(r.fonte) + ' nutrição estudo')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{color:'#ef4444',fontSize:'11px',fontStyle:'italic'}}
                >
                  {" — " + renderTextSafe(r.fonte)}
                </a>
              )}
            </>
          ) : renderTextSafe(r)}
        </li>
      ))}
    </ul>
  </div>
)}

          {/* BOTÃO COMPARTILHAR */}
          <button 
            className="share-btn"
            onClick={() => {
              const itens = plateItems.map(i => `• ${i.dish_display}`).join('\n');
              const beneficios = plateConsolidated?.beneficios?.slice(0, 2).join('\n• ') || '';
              const alergenos = [];
              if (plateConsolidated?.contemGluten) alergenos.push('Glúten');
              if (plateConsolidated?.contemLactose) alergenos.push('Lactose');
              
              const text = `🍽️ Minha refeição consciente:\n\n${itens}\n\n` +
                (beneficios ? `✅ Benefícios:\n• ${beneficios}\n\n` : '') +
                (alergenos.length > 0 ? `⚠️ Contém: ${alergenos.join(', ')}\n\n` : '') +
                `📊 ${plateConsolidated?.nutrition?.calorias} | ${plateConsolidated?.nutrition?.proteinas} proteínas\n\n` +
                `💡 Escolhi com informação! Conheça o SoulNutri - seu agente de nutrição virtual que te ajuda a fazer escolhas conscientes.\n\nsoulnutri.app.br`;
              
              if (navigator.share) {
                navigator.share({ title: 'SoulNutri - Minha Escolha', text });
              } else {
                navigator.clipboard.writeText(text);
                alert('Texto copiado!');
              }
            }}
          >
            📤 Compartilhar minha escolha
          </button>

          {/* BOTÕES DE AÇÃO */}
          <div className="mesa-actions">
            <button 
              className="add-more-btn"
              onClick={() => { clearResult(); setViewMode('buffet'); startCamera(); }}
            >
              + Adicionar mais itens
            </button>
            <button 
              className="clear-plate-btn"
              onClick={() => { clearPlate(); setViewMode('buffet'); startCamera(); }}
            >
              🗑️ Limpar prato
            </button>
          </div>
          
          {/* BOTÃO SAIR */}
          <button 
            className="exit-btn"
            onClick={() => { clearPlate(); clearResult(); setViewMode('buffet'); startCamera(); }}
            style={{
              marginTop: '16px',
              padding: '12px 24px',
              background: '#666',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              cursor: 'pointer',
              width: '100%'
            }}
            data-testid="exit-button"
          >
            ✕ Sair
          </button>
        </div>
      )}

      {/* Mini resumo do prato (quando está no buffet adicionando itens) */}
      {plateItems.length > 0 && viewMode === 'buffet' && !r && (
        <div className="plate-mini-summary" data-testid="plate-mini">
          <div className="mini-header">
            <span>🍽️ Seu Prato ({plateItems.length})</span>
            <span>{plateConsolidated?.nutrition?.calorias}</span>
          </div>
          <div className="mini-items">
            {plateItems.map((item) => (
              <span key={item.id} className="mini-tag">{item.dish_display}</span>
            ))}
          </div>
          <button 
            className="finish-plate-btn"
            onClick={() => setViewMode('mesa')}
          >
            ✓ Prato completo - Ver análise
          </button>
        </div>
      )}

      <input 
        ref={fileInputRef} 
        type="file" 
        accept="image/*"
        hidden 
        onChange={handleFileSelect} 
      />

      {/* RESULTADO */}
      {r?.ok && (
        <div className={`res ${r.confidence}`} data-testid="result-container">
          
          {/* BOTÃO VOLTAR */}
          <button 
            className="back-btn"
            onClick={() => { setResult(null); setPreviewImageUrl(null); setFeedbackSent(false); setShowCorrectionFlow(false); setCorrectionSearch(''); if(ttsAudioRef.current){ttsAudioRef.current.pause();ttsAudioRef.current=null;setTtsPlaying(false);} }}
            data-testid="back-btn"
          >
            ← Voltar
          </button>
          
          {/* BOTAO OUVIR - Acessibilidade (RÁPIDO: nome, nutrição, alertas) */}
          {r.identified && (
            <button
              className="tts-btn"
              data-testid="tts-listen-btn"
              onClick={() => playDishAudio({
                dish_display: r.dish_display,
                category: r.category,
                nutrition: r.nutrition,
                ingredientes: r.ingredientes,
                alergenos: r.alergenos,
                alertas_personalizados: r.alertas_personalizados,
                premium: r.premium ? { alertas_alergenos: r.premium.alertas_alergenos } : null,
              })}
              disabled={ttsLoading}
              aria-label={ttsPlaying ? 'Parar audio' : 'Ouvir descricao do prato'}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                width: '100%',
                padding: '14px 20px',
                margin: '8px 0 12px',
                background: ttsPlaying 
                  ? 'linear-gradient(135deg, #ef4444, #dc2626)' 
                  : 'linear-gradient(135deg, #10b981, #059669)',
                color: '#fff',
                border: 'none',
                borderRadius: '14px',
                fontSize: '16px',
                fontWeight: '700',
                cursor: ttsLoading ? 'wait' : 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: '0 4px 12px rgba(16,185,129,0.3)',
                minHeight: '52px',
              }}
            >
              {ttsLoading ? (
                <span>Gerando audio...</span>
              ) : ttsPlaying ? (
                <><span style={{fontSize:'20px'}}>&#9724;</span> Parar</>
              ) : (
                <><span style={{fontSize:'20px'}}>&#128266;</span> Ouvir</>
              )}
            </button>
          )}
          {ttsError && <div style={{color:'#ef4444',fontSize:'12px',textAlign:'center',margin:'4px 0'}}>{ttsError}</div>}
          
          {/* ALERTAS PREMIUM - Destaque na tela de resultado */}
          {r.premium?.alertas_alergenos?.length > 0 && (
            <div data-testid="result-allergen-alerts" style={{
              margin: '8px 0', padding: '12px 16px',
              background: 'linear-gradient(135deg, rgba(239,68,68,0.2), rgba(185,28,28,0.15))',
              border: '2px solid rgba(239,68,68,0.5)',
              borderRadius: '14px',
            }}>
              {r.premium.alertas_alergenos.map((al, i) => (
                <div key={i} style={{color:'#fca5a5',fontWeight:'bold',fontSize:'15px',padding:'4px 0'}}>
                  {renderTextSafe(al.icone)} {renderTextSafe(al.mensagem)}
                </div>
              ))}
            </div>
          )}
          {r.premium?.alertas_historico?.length > 0 && (
            <div data-testid="result-history-alerts" style={{
              margin: '8px 0', padding: '12px 16px',
              background: 'rgba(245,158,11,0.12)',
              border: '1px solid rgba(245,158,11,0.4)',
              borderRadius: '14px',
            }}>
              {r.premium.alertas_historico.map((al, i) => (
                <div key={i} style={{color:'#fcd34d',fontSize:'14px',padding:'2px 0'}}>
                  {renderTextSafe(al.alerta_breve || al.mensagem || al.texto || al)}
                </div>
              ))}
            </div>
          )}
          
          {/* Preview da imagem */}
          {previewImageUrl && (
            <div className="preview-image-container">
              <img src={previewImageUrl} alt="Foto do prato" className="preview-image" />
            </div>
          )}
          
          {/* FAMILIA DE PRATOS - bloco de substituicao desativado; exibicao complementar abaixo */}
          {false ? (
            <div className="family-ambiguity-box" data-testid="family-ambiguity-box" style={{
              background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.15), rgba(245, 158, 11, 0.08))',
              border: '2px solid rgba(245, 158, 11, 0.4)',
              borderRadius: '16px',
              padding: '20px',
              margin: '8px 0 16px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '24px', marginBottom: '8px' }}>&#x26A0;&#xFE0F;</div>
              {r.family_name ? (
                <h2 className="dish-name" data-testid="dish-name" style={{ fontSize: '18px', margin: '0 0 8px' }}>
                  Familia: {r.family_name}
                </h2>
              ) : (
                <h2 className="dish-name" data-testid="dish-name" style={{ fontSize: '18px', margin: '0 0 8px' }}>
                  Prato nao identificado com certeza
                </h2>
              )}
              <p style={{ fontSize: '13px', color: '#b45309', margin: '0 0 12px', fontWeight: '500' }}>
                Pratos visualmente similares detectados. Pode ser:
              </p>
              <div data-testid="family-candidates-list" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {(r.family_candidates_detail || r.family_candidates.map(c => ({ nome: c }))).map((candidate, i) => (
                  <div key={i} data-testid={`family-candidate-${i}`} style={{
                    background: 'rgba(255,255,255,0.85)',
                    borderRadius: '12px',
                    padding: '12px 16px',
                    textAlign: 'left',
                    border: '1px solid rgba(245, 158, 11, 0.25)'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: candidate.ingredientes?.length ? '6px' : '0' }}>
                      <span style={{ fontSize: '15px', fontWeight: '700', color: '#92400e' }}>
                        {candidate.categoria_emoji || ''} {candidate.nome || candidate}
                      </span>
                      {candidate.categoria && (
                        <span style={{ fontSize: '10px', color: '#a16207', background: 'rgba(245,158,11,0.15)', padding: '2px 8px', borderRadius: '8px', fontWeight: '600', textTransform: 'uppercase' }}>
                          {candidate.categoria}
                        </span>
                      )}
                    </div>
                    {candidate.ingredientes && candidate.ingredientes.length > 0 && (
                      <div style={{ fontSize: '12px', color: '#78716c', lineHeight: '1.4' }}>
                        {candidate.ingredientes.slice(0, 5).join(', ')}
                        {candidate.ingredientes.length > 5 ? '...' : ''}
                      </div>
                    )}
                    {candidate.descricao && (
                      <div style={{ fontSize: '11px', color: '#a8a29e', marginTop: '4px', fontStyle: 'italic' }}>
                        {candidate.descricao.length > 80 ? candidate.descricao.substring(0, 80) + '...' : candidate.descricao}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <p style={{ fontSize: '12px', color: '#78716c', margin: '12px 0 0', fontStyle: 'italic' }}>
                Confirme pela plaquinha ou com o atendente
              </p>
            </div>
          ) : (
            <>
              {/* Nome do Prato — v1.3: exibe diferente conforme confiança */}
              {r.confidence === 'baixa' && !r.identified ? (
                <div data-testid="not-recognized-box" style={{
                  background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1), rgba(220, 38, 38, 0.05))',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: '16px',
                  padding: '20px',
                  margin: '8px 0 16px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '16px', fontWeight: '600', color: '#dc2626', marginBottom: '6px' }}>
                    Prato nao reconhecido
                  </div>
                  <div style={{ fontSize: '13px', color: '#888' }}>
                    Tente outra foto ou consulte o atendente
                  </div>
                </div>
              ) : (r.confidence === 'média' || r.confidence === 'media') ? (
                <div data-testid="medium-confidence-box">
                  <div style={{
                    background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.12), rgba(245, 158, 11, 0.06))',
                    border: '1px solid rgba(245, 158, 11, 0.35)',
                    borderRadius: '16px',
                    padding: '16px 20px',
                    margin: '8px 0 12px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '15px', color: '#b45309', marginBottom: '6px', fontWeight: '600' }}>
                      Parece ser:
                    </div>
                    <h2 className="dish-name" data-testid="dish-name" style={{ margin: '0 0 8px', fontSize: '24px' }}>
                      {r.family_name && ['milanesa', 'batata', 'sobrecoxa', 'gratinados'].includes((r.family_slug || '').toLowerCase())
                        ? r.family_name
                        : r.dish_display}
                    </h2>
                    <div style={{ fontSize: '13px', color: '#a16207' }}>
                      Verifique se o prato esta correto ({Math.round(r.score * 100)}%)
                    </div>
                    {(r.nutrition?.calorias || r.calorias_estimadas) && (
                      <div data-testid="calories-highlight" style={{ fontSize: '12px', color: '#92400e', marginTop: '6px', fontWeight: '500' }}>
                        {r.nutrition?.calorias || r.calorias_estimadas}
                        {r.nutrition?.proteinas && ` • P: ${r.nutrition.proteinas}`}
                      </div>
                    )}
                  </div>
                  {!r.family_name && r.alternatives && r.alternatives.length > 0 && (
                    <div data-testid="alternatives-list" style={{ margin: '8px 0' }}>
                      <div style={{ fontSize: '12px', color: '#888', marginBottom: '6px', textAlign: 'center' }}>
                        Tambem pode ser:
                      </div>
                      {r.alternatives.slice(0, 2).map((alt, i) => (
                        <div key={i} data-testid={`alternative-${i}`} style={{
                          background: 'rgba(245, 158, 11, 0.08)',
                          border: '1px solid rgba(245, 158, 11, 0.2)',
                          borderRadius: '10px',
                          padding: '8px 14px',
                          marginBottom: '4px',
                          fontSize: '14px',
                          fontWeight: '500',
                          color: '#92400e',
                          textAlign: 'center'
                        }}>
                          {renderTextSafe(alt)}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <h2 className="dish-name" data-testid="dish-name">
                    {r.family_name && ['milanesa', 'batata', 'sobrecoxa', 'gratinados'].includes((r.family_slug || '').toLowerCase())
                      ? r.family_name
                      : r.dish_display}
                  </h2>
                  {(r.nutrition?.calorias || r.calorias_estimadas) && (
                    <div
                      className="calories-highlight"
                      data-testid="calories-highlight"
                      style={{
                        background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.2), rgba(217, 119, 6, 0.1))',
                        borderRadius: '12px',
                        padding: '12px 20px',
                        margin: '8px 0 4px',
                        textAlign: 'center'
                      }}
                    >
                      <span style={{ fontSize: '28px', fontWeight: 'bold', color: '#f59e0b' }}>
                        {r.nutrition?.calorias || r.calorias_estimadas}
                      </span>
                      <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>
                        {r.nutrition?.proteinas && `P: ${r.nutrition.proteinas}`}
                        {r.nutrition?.carboidratos && ` • C: ${r.nutrition.carboidratos}`}
                        {r.nutrition?.gorduras && ` • G: ${r.nutrition.gorduras}`}
                      </div>
                    </div>
                  )}
                </>
              )}
              
              {/* FAMÍLIA DE PRATOS — imediatamente após resultado principal (ordem global) */}
              {(() => {
                const filteredCandidates = r.family_candidates || [];
                if (!r.family_name || filteredCandidates.length === 0) return null;
                return (
                  <div
                    data-testid="family-block"
                    style={{
                      background: 'rgba(251, 191, 36, 0.08)',
                      border: '1px solid rgba(251, 191, 36, 0.35)',
                      borderRadius: '12px',
                      padding: '12px 16px',
                      margin: '8px 0',
                    }}
                  >
                    <div style={{ marginBottom: '6px' }}>
                      <span data-testid="family-name-label" style={{ fontSize: '14px', fontWeight: '700', color: '#fbbf24' }}>
                        Família visual: {r.family_name}
                      </span>
                    </div>
                    <p style={{ fontSize: '12px', color: '#94a3b8', margin: '0 0 4px 0', lineHeight: '1.5' }}>
                      Pratos visualmente semelhantes no buffet:
                    </p>
                    <ul data-testid="family-candidates-list" style={{ margin: '0', padding: '0 0 0 16px', fontSize: '13px', color: '#cbd5e1', lineHeight: '1.9' }}>
                      {filteredCandidates.map((c, i) => (
                        <li key={i} data-testid={`family-candidate-${i}`}>{c}</li>
                      ))}
                    </ul>
                    <p data-testid="family-pastel-note" style={{ fontSize: '12px', color: '#fbbf24', margin: '6px 0 0 0', lineHeight: '1.5', fontStyle: 'italic' }}>
                      Confirme o nome na plaquinha ou com o atendente.
                    </p>
                  </div>
                );
              })()}

              {/* NÍVEL DE CONFIANÇA - Sistema de 3 níveis */}
              {r.confidence_level && r.confidence !== 'baixa' && (
                <div 
                  className={`confidence-level-badge ${r.confidence}`}
                  style={{ 
                    background: confData.bg, 
                    color: confData.color,
                    border: `1px solid ${confData.color}`,
                    padding: '8px 16px',
                    borderRadius: '20px',
                    fontSize: '14px',
                    fontWeight: '600',
                    margin: '8px 0',
                    textAlign: 'center'
                  }}
                  data-testid="confidence-level-badge"
                >
                  {r.confidence_level} ({Math.round(r.score * 100)}%)
                </div>
              )}

              {radarInfo?.has_alert && (
                <div
                  data-testid="radar-alimentar-main"
                  style={{
                    background: 'rgba(239, 68, 68, 0.12)',
                    border: '1px solid rgba(239, 68, 68, 0.45)',
                    borderLeft: '4px solid #ef4444',
                    borderRadius: '12px',
                    padding: '12px',
                    margin: '12px 0',
                    color: '#fff',
                  }}
                >
                  <div style={{ fontWeight: 800, fontSize: '12px', marginBottom: '6px' }}>
                    ⚠️ RADAR ALIMENTAR — ALERTA
                  </div>
                  <div style={{ fontSize: '14px', lineHeight: '1.4', fontWeight: 600 }}>
                    {renderTextSafe(radarInfo.titulo || radarInfo.message)}
                  </div>
                  {radarInfo.fonte && (
                    <div style={{ color: '#cbd5e1', fontSize: '12px', marginTop: '6px' }}>
                      Fonte: {renderTextSafe(radarInfo.fonte)}
                    </div>
                  )}
                  {radarInfo.url && (
                    <a
                      href={radarInfo.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'inline-block',
                        color: '#fca5a5',
                        fontSize: '12px',
                        marginTop: '6px',
                      }}
                    >
                      Ler notícia original →
                    </a>
                  )}
                </div>
              )}
              {/* ALTERNATIVAS — exibir após família quando family_name presente */}
              {r.family_name && (r.confidence === 'média' || r.confidence === 'media') &&
               r.alternatives && r.alternatives.length > 0 && (
                <div data-testid="alternatives-list" style={{ margin: '8px 0' }}>
                  <div style={{ fontSize: '12px', color: '#888', marginBottom: '6px', textAlign: 'center' }}>
                    Tambem pode ser:
                  </div>
                  {r.alternatives.slice(0, 2).map((alt, i) => (
                    <div key={i} data-testid={`alternative-${i}`} style={{
                      background: 'rgba(245, 158, 11, 0.08)',
                      border: '1px solid rgba(245, 158, 11, 0.2)',
                      borderRadius: '10px',
                      padding: '8px 14px',
                      marginBottom: '4px',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#92400e',
                      textAlign: 'center'
                    }}>
                      {renderTextSafe(alt)}
                    </div>
                  ))}
                </div>
              )}

              {/* CATEGORIA - não mostrar na baixa */}
              {r.confidence !== 'baixa' && (
                <div 
                  className="category-badge" 
                  style={{ background: catStyle.bg, color: catStyle.color, border: catStyle.border || 'none' }}
                  data-testid="category-badge"
                >
                  {r.category_emoji} {String(r.category ?? '').toUpperCase()}
                </div>
              )}
            </>
          )}

          {/* VISTA BUFFET - Informações para DECISÃO RÁPIDA */}
          
          {/* ALÉRGENOS */}
          <div className={`allergen-section ${allergenInfo.hasAllergens ? 'has-allergens' : 'no-allergens'}`}>
            {allergenInfo.hasAllergens ? (
              allergenInfo.alerts.map((alert, i) => (
                <div key={i} className={`alert-item ${alert.type}`}>
                  ⚠️ {renderTextSafe(alert.text)}
                </div>
              ))
            ) : (
              <div className="alert-item safe">
                {allergenInfo.text}
              </div>
            )}
          </div>

          {/* DICAS PERSONALIZADAS - Baseadas no perfil do usuário Premium */}
          {premiumUser?.perfil && (() => {
            const tips = generatePersonalizedTip(r, premiumUser.perfil);
            if (tips && tips.length > 0) {
              return (
                <div className="personalized-tips" data-testid="personalized-tips">
                  <h4>💡 Para você</h4>
                  {tips.map((tip, i) => (
                    <div key={i} className={`tip-item ${tip.tipo}`}>
                      <span className="tip-icon">{tip.icone}</span>
                      <span className="tip-text">{tip.texto}</span>
                    </div>
                  ))}
                </div>
              );
            }
            return null;
          })()}

          {/* Ingredientes */}
          {r.ingredientes?.length > 0 && (
            <div className="info-box" data-testid="ingredients-box">
              <h4>🥗 Ingredientes</h4>
              <p>{Array.isArray(r.ingredientes) ? r.ingredientes.join(', ') : r.ingredientes}</p>
            </div>
          )}

          {/* Benefícios - Resumo rápido */}
          {r.beneficios?.length > 0 && (
            <div className="info-box good" data-testid="benefits-box">
              <h4>✅ Benefícios</h4>
<ul>{r.beneficios.slice(0, 2).map((b,i) => 
  <li key={i}>
    {typeof b === 'object' ? (
      <>
        {renderTextSafe(b.texto)}
        {b.fonte && (
          <a 
            href={`https://www.google.com/search?q=${encodeURIComponent(renderTextSafe(b.fonte) + ' nutrição estudo')}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{color:'#10b981',fontSize:'11px',fontStyle:'italic'}}
          >
            {" — " + renderTextSafe(b.fonte)}
          </a>
        )}
      </>
    ) : renderTextSafe(b)}
  </li>
)}</ul>
            </div>
          )}

          {/* Referências e Fontes */}
          {r.confidence !== 'baixa' && (
            <div className="info-box" data-testid="nutrition-references" style={{
              background: 'rgba(124, 58, 237, 0.08)',
              border: '1px solid rgba(124, 58, 237, 0.2)',
              borderRadius: '12px',
              padding: '12px 16px',
              marginTop: '8px'
            }}>
              <h4 style={{ color: '#c4b5fd', fontSize: '13px', marginBottom: '8px' }}>Fontes Nutricionais</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <a href="https://www.cfn.org.br/wp-content/uploads/2017/03/taco_4_edicao_ampliada_e_revisada.pdf"
                   target="_blank" rel="noopener noreferrer"
                   data-testid="ref-taco"
                   style={{ color: '#94a3b8', fontSize: '12px', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ color: '#10b981' }}>↗</span> TACO - Tabela Brasileira (UNICAMP)
                </a>
                <a href="https://fdc.nal.usda.gov/food-search"
                   target="_blank" rel="noopener noreferrer"
                   data-testid="ref-usda"
                   style={{ color: '#94a3b8', fontSize: '12px', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ color: '#10b981' }}>↗</span> USDA FoodData Central
                </a>
                <a href="https://world.openfoodfacts.org/"
                   target="_blank" rel="noopener noreferrer"
                   data-testid="ref-off"
                   style={{ color: '#94a3b8', fontSize: '12px', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ color: '#10b981' }}>↗</span> Open Food Facts
                </a>
              </div>
            </div>
          )}

          {/* ALERTA DE ALÉRGENOS - Visível para TODOS durante scan (fase buffet) */}
          {(() => {
            const alergenos = [];
            if (r.contem_gluten || r.alergenos?.gluten) alergenos.push({ icone: '🌾', nome: 'Glúten' });
            if (r.contem_lactose || r.alergenos?.lactose) alergenos.push({ icone: '🥛', nome: 'Lactose' });
            if (r.contem_ovo || r.alergenos?.ovo) alergenos.push({ icone: '🥚', nome: 'Ovo' });
            if (r.contem_frutos_mar || r.alergenos?.frutos_mar || r.alergenos?.frutosMar) alergenos.push({ icone: '🦐', nome: 'Frutos do Mar' });
            if (r.contem_castanhas || r.alergenos?.castanhas) alergenos.push({ icone: '🥜', nome: 'Castanhas' });
            if (r.contem_soja || r.alergenos?.soja) alergenos.push({ icone: '🫘', nome: 'Soja' });
            
            if (alergenos.length > 0) {
              return (
                <div data-testid="scan-allergen-alert" style={{
                  background: 'rgba(245, 158, 11, 0.15)',
                  border: '2px solid #f59e0b',
                  borderRadius: '12px',
                  padding: '12px',
                  marginTop: '8px'
                }}>
                  <h4 style={{ color: '#fbbf24', margin: '0 0 8px', fontSize: '14px' }}>
                    ⚠️ Alérgenos detectados
                  </h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {alergenos.map((a, i) => (
                      <span key={i} style={{
                        background: 'rgba(245, 158, 11, 0.25)',
                        color: '#fbbf24',
                        padding: '4px 10px',
                        borderRadius: '20px',
                        fontSize: '13px',
                        fontWeight: 'bold'
                      }}>
                        {a.icone} {a.nome}
                      </span>
                    ))}
                  </div>
                </div>
              );
            }
            return null;
          })()}

          {/* ALERTA PERSONALIZADO - Baseado nas restrições do perfil Premium */}
          {premiumUser && (() => {
            const restricoes = premiumUser.restricoes || [];
            const alertasPersonalizados = [];
            
            // Verificar glúten
            if (restricoes.includes('sem_gluten') && (r.contem_gluten || r.alergenos?.gluten)) {
              alertasPersonalizados.push({ icone: '🌾', texto: 'Contém GLÚTEN - você registrou restrição!' });
            }
            // Verificar lactose
            if (restricoes.includes('sem_lactose') && (r.contem_lactose || r.alergenos?.lactose)) {
              alertasPersonalizados.push({ icone: '🥛', texto: 'Contém LACTOSE - você registrou restrição!' });
            }
            // Verificar ovo
            if (restricoes.includes('sem_ovo') && (r.contem_ovo || r.alergenos?.ovo)) {
              alertasPersonalizados.push({ icone: '🥚', texto: 'Contém OVO - você registrou restrição!' });
            }
            // Verificar frutos do mar
            if (restricoes.includes('sem_frutos_mar') && (r.contem_frutos_mar || r.alergenos?.frutos_mar || r.alergenos?.frutosMar)) {
              alertasPersonalizados.push({ icone: '🦐', texto: 'Contém FRUTOS DO MAR - você registrou restrição!' });
            }
            // Verificar oleaginosas/castanhas
            if (restricoes.includes('sem_oleaginosas') && (r.contem_castanhas || r.alergenos?.castanhas)) {
              alertasPersonalizados.push({ icone: '🥜', texto: 'Contém OLEAGINOSAS - você registrou restrição!' });
            }
            // Verificar soja
            if (restricoes.includes('sem_soja') && (r.contem_soja || r.alergenos?.soja)) {
              alertasPersonalizados.push({ icone: '🫘', texto: 'Contém SOJA - você registrou restrição!' });
            }
            // Verificar vegano/vegetariano
            if ((restricoes.includes('vegano') || restricoes.includes('vegetariano')) && 
                (r.category === 'proteína animal' || r.ingredientes?.some(i => 
                  i.toLowerCase().includes('carne') || i.toLowerCase().includes('frango') || 
                  i.toLowerCase().includes('peixe') || i.toLowerCase().includes('bacon')))) {
              alertasPersonalizados.push({ icone: '🥬', texto: 'Contém PROTEÍNA ANIMAL - você é vegetariano/vegano!' });
            }
            
            if (alertasPersonalizados.length > 0) {
              return (
                <div className="personal-alert-box" data-testid="personal-alert" style={{
                  background: 'rgba(239, 68, 68, 0.15)',
                  border: '2px solid #ef4444',
                  borderRadius: '12px',
                  padding: '12px',
                  marginTop: '8px'
                }}>
                  <h4 style={{ color: '#ef4444', margin: '0 0 8px', fontSize: '14px' }}>
                    ⚠️ ATENÇÃO - Restrição no seu perfil!
                  </h4>
                  {alertasPersonalizados.map((alerta, i) => (
                    <div key={i} style={{ 
                      color: '#fca5a5', 
                      fontSize: '13px', 
                      marginBottom: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      <span>{alerta.icone}</span>
                      <span>{alerta.texto}</span>
                    </div>
                  ))}
                </div>
              );
            }
            return null;
          })()}

          {/* BOTÕES DE AÇÃO - BUFFET */}
<div className="buffet-actions">
  <button 
    className="add-to-plate-btn"
    onClick={() => setShowAddMore(true)}
    data-testid="add-to-plate-btn"
  >
    ✓ Adicionar ao prato
  </button>

  {canShowAdminTools && !showCorrectionFlow && (
    <button
      type="button"
      onClick={() => setShowCorrectionFlow(true)}
      data-testid="admin-tools-btn"
      style={{
        marginTop: '10px',
        background: 'transparent',
        color: '#cbd5e1',
        border: '1px solid rgba(255,255,255,0.14)',
        borderRadius: '10px',
        padding: '9px 12px',
        fontSize: '13px',
        width: '100%'
      }}
    >
      Ferramentas do administrador
    </button>
  )}
</div>


          {/* FLUXO DE CORREÇÃO - 3 opções */}
          {canShowAdminTools && showCorrectionFlow && !feedbackSent && (
            <div className="feedback-section correction-flow" data-testid="correction-flow">
              <p className="feedback-question">Qual é o prato correto?</p>
              
              <input
                type="text"
                value={correctionSearch}
                onChange={e => setCorrectionSearch(e.target.value)}
                placeholder="Buscar prato..."
                data-testid="correction-search-input"
                style={{
                  width: '100%', padding: '10px 14px', background: 'rgba(255,255,255,0.08)',
                  color: '#fff', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '10px',
                  fontSize: '15px', marginBottom: '8px', boxSizing: 'border-box'
                }}
              />
              
              <div style={{
                maxHeight: '200px', overflowY: 'auto', marginBottom: '10px',
                borderRadius: '10px', border: '1px solid rgba(255,255,255,0.1)'
              }}>
                {dishes
                  .filter(d => {
                    const name = (d.nome || d.name || d.slug || '').toLowerCase();
                    return !correctionSearch || name.includes(correctionSearch.toLowerCase());
                  })
                  .sort((a, b) => (a.nome || a.name || '').localeCompare(b.nome || b.name || ''))
                  .slice(0, correctionSearch ? 50 : 206)
                  .map(d => (
                    <button
                      key={d.slug}
                      onClick={() => sendCorrectionWithDish(d.nome || d.name || d.slug)}
                      data-testid={`correction-dish-${d.slug}`}
                      style={{
                        display: 'block', width: '100%', padding: '10px 14px', textAlign: 'left',
                        background: 'transparent', color: '#e2e8f0', border: 'none',
                        borderBottom: '1px solid rgba(255,255,255,0.05)', cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      {d.nome || d.name || d.slug}
                    </button>
                  ))
                }
              </div>

              <div className="feedback-btns" style={{ gap: '8px' }}>
                <button 
                  className="fb-btn incorrect" 
                  onClick={sendCorrectionNotInList} 
                  data-testid="correction-not-in-list-btn"
                  style={{ fontSize: '14px', padding: '10px' }}
                >
                  Prato não está na lista
                </button>
                <button 
                  className="fb-btn back" 
                  onClick={() => { setShowCorrectionFlow(false); setCorrectionSearch(''); }} 
                  data-testid="correction-cancel-btn"
                  style={{ fontSize: '14px', padding: '10px' }}
                >
                  Cancelar
                </button>
              </div>
            </div>
          )}

          {feedbackSent && (
            <div style={{ textAlign: 'center', padding: '12px', color: '#22c55e', fontSize: '14px' }} data-testid="feedback-sent-msg">
              Registrado na calibração
            </div>
          )}

          <div className="time" data-testid="response-time">⚡ {r.search_time_ms?.toFixed(0)}ms</div>
        </div>
      )}

      {/* RESULTADO MULTI-ITEM */}
      {multiResult?.ok && (() => {
        // Determinar item principal (proteína > vegetariano > vegano)
        const itens = multiResult.itens || [];
        const proteinas = itens.filter(i => i.categoria === 'proteína animal');
        const vegetarianos = itens.filter(i => i.categoria === 'vegetariano');
        const veganos = itens.filter(i => i.categoria === 'vegano');
        
        const principal = proteinas[0] || vegetarianos[0] || veganos[0] || itens[0];
        const acompanhamentos = itens.filter(i => i.nome !== principal?.nome);
        
        const getCategoryEmoji = (cat) => {
          if (cat === 'proteína animal') return '🍖';
          if (cat === 'vegetariano') return '🥚';
          if (cat === 'vegano') return '🥬';
          return '🍽️';
        };
        
        return (
          <div className="res multi-result" data-testid="multi-result-container">
            
            {/* Preview da imagem selecionada da galeria */}
            {previewImageUrl && (
              <div className="preview-image-container">
                <img src={previewImageUrl} alt="Foto do prato" className="preview-image" />
              </div>
            )}
            
            {/* Header com item principal */}
            {principal && (
              <div className="prato-principal">
                <span className="principal-emoji">{getCategoryEmoji(principal.categoria)}</span>
                <div className="principal-info">
                  <h2 className="principal-nome">{principal.nome}</h2>
                  {acompanhamentos.length > 0 && (
                    <span className="acompanhamentos-count">+ {acompanhamentos.length} acompanhamento{acompanhamentos.length > 1 ? 's' : ''}</span>
                  )}
                </div>
              </div>
            )}
            
            {/* Lista de acompanhamentos */}
            {acompanhamentos.length > 0 && (
              <div className="acompanhamentos-lista">
                {acompanhamentos.map((item, idx) => (
                  <span key={idx} className={`acomp-tag ${item.categoria?.replace(' ', '-')}`}>
                    {getCategoryEmoji(item.categoria)} {item.nome}
                  </span>
                ))}
              </div>
            )}

            {/* Resumo nutricional */}
            {multiResult.resumo_nutricional && (
              <div className="multi-summary" data-testid="multi-summary">
                <div className="summary-grid">
                  <div className="summary-item">
                    <span className="summary-value">{multiResult.resumo_nutricional.calorias_totais || '~' + (itens.length * 150) + ' kcal'}</span>
                    <span className="summary-label">Calorias</span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-value">{multiResult.resumo_nutricional.proteinas_totais || '~' + (proteinas.length * 20) + 'g'}</span>
                    <span className="summary-label">Proteínas</span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-value">{multiResult.resumo_nutricional.carboidratos_totais || '~' + (veganos.length * 15) + 'g'}</span>
                    <span className="summary-label">Carbos</span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-value">{multiResult.resumo_nutricional.gorduras_totais || '~10g'}</span>
                    <span className="summary-label">Gorduras</span>
                  </div>
                </div>
              </div>
            )}

            {/* Equilíbrio */}
            {multiResult.equilibrio && (
              <div className={`equilibrio-badge ${multiResult.equilibrio}`}>
                {multiResult.equilibrio === 'balanceado' && '⚖️ Refeição Balanceada'}
                {multiResult.equilibrio === 'rico_em_carboidratos' && '🍞 Rico em Carboidratos'}
                {multiResult.equilibrio === 'rico_em_proteinas' && '💪 Rico em Proteínas'}
                {multiResult.equilibrio === 'rico_em_gorduras' && '🧈 Rico em Gorduras'}
              </div>
            )}

            {/* Alertas */}
            {multiResult.alertas_combinados?.length > 0 && (
              <div className="multi-alerts">
                {multiResult.alertas_combinados.map((alert, i) => (
                  <span key={i} className="alert-tag">⚠️ {renderTextSafe(alert)}</span>
                ))}
              </div>
            )}

            {/* Dica */}
            {multiResult.dica_nutricional && (
              <div className="dica-box">
                <p>💡 {multiResult.dica_nutricional}</p>
              </div>
            )}

            <div className="time" data-testid="multi-response-time">
              ⚡ {multiResult.search_time_ms?.toFixed(0)}ms
            </div>

            {/* Compartilhar */}
            <button 
              className="share-btn"
              onClick={() => {
                const acompText = acompanhamentos.map(i => i.nome).join(', ');
                const text = `🍽️ Meu prato no Cibi Sana:\n\n${getCategoryEmoji(principal?.categoria)} ${principal?.nome}${acompText ? `\n+ ${acompText}` : ''}\n\n📊 ${multiResult.resumo_nutricional?.calorias_totais || 'Calculando calorias...'}\n\nAnalisado pelo SoulNutri!`;
                if (navigator.share) {
                  navigator.share({ title: 'SoulNutri', text });
                } else {
                  navigator.clipboard.writeText(text);
                  alert('Texto copiado! Cole para compartilhar.');
                }
              }}
              data-testid="multi-share-button"
            >
              📤 Compartilhar meu prato
            </button>

            {/* BOTÕES DE FEEDBACK - MULTI - Removido do fluxo de montagem */}
            {/* A correção fica disponível via botão de editar se necessário */}
            {feedbackSent && (
              <div className="feedback-thanks">
                ✅ Prato registrado!
              </div>
            )}
          </div>
        );
      })()}

      {/* MODAL "ADICIONAR MAIS ITENS?" - Fluxo Único Inteligente */}
      {showAddMore && result?.ok && result?.identified && (
        <div className="modal-overlay add-more-overlay" data-testid="add-more-modal">
          <div className="modal-content add-more-modal" onClick={e => e.stopPropagation()}>
            {/* BOTÃO VOLTAR */}
            <button 
              className="modal-back-btn"
              onClick={() => setShowAddMore(false)}
            >
              ← Voltar
            </button>
            
            <div className="add-more-success">
              <span className="add-more-emoji">✅</span>
              <h3>
                {result.family_name && ['milanesa', 'batata', 'sobrecoxa', 'gratinados'].includes((result.family_slug || '').toLowerCase())
                  ? result.family_name
                  : result.dish_display}
              </h3>
              <p className="add-more-confidence">
                {result.confidence === 'alta' ? '🎯 Alta confiança' : 
                 result.confidence === 'media' ? '👍 Boa confiança' : '🤔 Verificar'}
              </p>
            </div>

            <div className="add-more-question">
              <p>Seu prato está completo?</p>
              {plateItems.length > 0 && (
                <small className="plate-count">
                  ({plateItems.length} {plateItems.length === 1 ? 'item' : 'itens'} já adicionado{plateItems.length > 1 ? 's' : ''})
                </small>
              )}
            </div>
            
            <div className="add-more-actions">
              <button 
                className="add-more-finish-btn"
                onClick={finishPlate}
                data-testid="finish-plate-btn"
              >
                ✓ Sim, está completo
              </button>
              <button 
                className="add-more-continue-btn"
                onClick={addItemToPlate}
                data-testid="add-more-btn"
              >
                + Adicionar mais itens
              </button>
            </div>
            
            <p className="add-more-tip">
              💡 Dica: Para buffet, fotografe cada item separadamente
            </p>
          </div>
        </div>
      )}

      {/* MODAL RADAR DE NOTÍCIAS - Detalhes */}
      {showRadarDetails && radarInfo && (
        <div className="modal-overlay" onClick={() => setShowRadarDetails(false)}>
          <div className="modal-content radar-details" onClick={e => e.stopPropagation()} style={{
            maxWidth: '400px',
            background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
            borderRadius: '16px',
            padding: '20px'
          }}>
            <button 
              className="modal-back-btn"
              onClick={() => setShowRadarDetails(false)}
              style={{ position: 'absolute', top: '10px', left: '10px' }}
            >
              ← Voltar
            </button>
            
            <div style={{ textAlign: 'center', marginBottom: '20px', marginTop: '20px' }}>
              <span style={{ fontSize: '32px' }}>{radarInfo.emoji || '📰'}</span>
              <h3 style={{ color: '#fff', margin: '10px 0 0' }}>Radar Nutricional</h3>
              <p style={{ color: '#888', fontSize: '12px' }}>Informações sobre este alimento</p>
            </div>
            
            {radarInfo.titulo && (
              <div style={{
                background: 'rgba(34, 197, 94, 0.1)',
                padding: '12px',
                borderRadius: '10px',
                marginBottom: '12px'
              }}>
                <h4 style={{ color: '#22c55e', margin: '0 0 6px', fontSize: '14px' }}>
                  {renderTextSafe(radarInfo.titulo)}
                </h4>
                <p style={{ color: '#ccc', fontSize: '13px', margin: 0, lineHeight: '1.5' }}>
                  {renderTextSafe(radarInfo.message)}
                </p>
                {radarInfo.fonte && (
                  <p style={{ color: '#888', fontSize: '11px', margin: '8px 0 0' }}>
                    📚 Fonte: {renderTextSafe(radarInfo.fonte)}
                  </p>
                )}
              </div>
            )}
            
            {/* Você Sabia? */}
            {radarInfo.voce_sabia && (
              <div style={{
                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(139, 92, 246, 0.2))',
                padding: '12px',
                borderRadius: '10px',
                marginTop: '12px',
                borderLeft: '3px solid #3b82f6'
              }}>
                <h4 style={{ color: '#3b82f6', margin: '0 0 6px', fontSize: '12px' }}>
                  💡 Você sabia?
                </h4>
                <p style={{ color: '#e0e0e0', fontSize: '12px', margin: 0, lineHeight: '1.5' }}>
                  {renderTextSafe(radarInfo.voce_sabia)}
                </p>
              </div>
            )}
            
            {/* Combinações Benéficas */}
            {radarInfo.combinacoes?.length > 0 && (
              <div style={{
                background: 'rgba(34, 197, 94, 0.1)',
                padding: '12px',
                borderRadius: '10px',
                marginTop: '12px'
              }}>
                <h4 style={{ color: '#22c55e', margin: '0 0 8px', fontSize: '12px' }}>
                  🔗 Combinações que potencializam nutrientes:
                </h4>
                {radarInfo.combinacoes.map((combo, i) => (
                  <p key={i} style={{ color: '#ccc', fontSize: '11px', margin: '4px 0', paddingLeft: '8px' }}>
                    • {renderTextSafe(combo)}
                  </p>
                ))}
              </div>
            )}
            
            {radarInfo.facts?.length > 0 && (
              <div style={{ marginTop: '16px' }}>
                <h4 style={{ color: '#fff', fontSize: '13px', marginBottom: '10px' }}>
                  📌 Mais informações
                </h4>
                {radarInfo.facts.map((grupo, i) => (
                  <div key={i} style={{ marginBottom: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                      <span>{grupo.emoji}</span>
                      <span style={{ color: '#fff', fontWeight: 'bold', fontSize: '13px' }}>
                        {renderTextSafe(grupo.ingrediente)}
                      </span>
                    </div>
                    {grupo.voce_sabia && !radarInfo.voce_sabia && (
                      <p style={{ 
                        color: '#3b82f6', 
                        fontSize: '11px', 
                        margin: '0 0 6px',
                        paddingLeft: '22px'
                      }}>
                        💡 {renderTextSafe(grupo.voce_sabia)}
                      </p>
                    )}
                    {grupo.dica_rapida && (
                      <p style={{ 
                        color: '#22c55e', 
                        fontSize: '12px', 
                        margin: '0 0 8px',
                        paddingLeft: '22px'
                      }}>
                        ✓ {renderTextSafe(grupo.dica_rapida)}
                      </p>
                    )}
                    {grupo.fatos?.map((fato, j) => (
                      <div key={j} style={{
                        background: fato.tipo === 'alerta' ? 'rgba(239, 68, 68, 0.1)' : 
                                   fato.tipo === 'beneficio' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(255,255,255,0.05)',
                        padding: '10px',
                        borderRadius: '8px',
                        marginBottom: '6px',
                        marginLeft: '22px',
                        borderLeft: fato.tipo === 'alerta' ? '3px solid #ef4444' : 
                                   fato.tipo === 'beneficio' ? '3px solid #22c55e' : '3px solid #666'
                      }}>
                        <p style={{ 
                          color: fato.tipo === 'alerta' ? '#f87171' : 
                                fato.tipo === 'beneficio' ? '#4ade80' : '#fff', 
                          fontSize: '12px', 
                          fontWeight: 'bold', 
                          margin: '0 0 4px' 
                        }}>
                          {fato.tipo === 'alerta' ? '⚠️ ' : fato.tipo === 'beneficio' ? '✅ ' : ''}{renderTextSafe(fato.titulo)}
                        </p>
                        <p style={{ color: '#ccc', fontSize: '11px', margin: 0, lineHeight: '1.4' }}>
                          {renderTextSafe(fato.resumo)}
                        </p>
                        <p style={{ color: '#666', fontSize: '10px', margin: '4px 0 0' }}>
                          📚 {renderTextSafe(fato.fonte)} • {renderTextSafe(fato.data)}
                        </p>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
            
            <button 
              onClick={() => setShowRadarDetails(false)}
              style={{
                width: '100%',
                padding: '12px',
                background: '#22c55e',
                color: '#fff',
                border: 'none',
                borderRadius: '10px',
                fontWeight: 'bold',
                cursor: 'pointer',
                marginTop: '16px'
              }}
            >
              Entendi
            </button>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════════
          MODAL — RADAR ALIMENTAR (Breaking News Contextual, Camada 1 Premium)
          Abre ao tocar na faixa <RadarAlimentarStrip>
          ════════════════════════════════════════════════════════════════════ */}
      {breakingNewsModal && (() => {
        const bn = breakingNewsModal;
        const pol = bn.polaridade || 'neutro';
        const palette = pol === 'alerta'
          ? { chip: '#ef4444', icon: '⚠️', label: 'Alerta' }
          : pol === 'beneficio'
            ? { chip: '#22c55e', icon: '🟢', label: 'Benefício' }
            : { chip: '#fbbf24', icon: '📡', label: 'Informativo' };
        return (
          <div
            className="modal-overlay"
            data-testid="radar-alimentar-modal"
            onClick={() => setBreakingNewsModal(null)}
          >
            <div
              className="modal-content radar-details"
              onClick={(e) => e.stopPropagation()}
              style={{
                maxWidth: '420px',
                background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
                borderRadius: '16px',
                padding: '20px',
                position: 'relative',
              }}
            >
              <button
                className="modal-back-btn"
                onClick={() => setBreakingNewsModal(null)}
                data-testid="radar-alimentar-modal-close"
                style={{ position: 'absolute', top: '10px', left: '10px' }}
              >
                ← Voltar
              </button>

              <div style={{ textAlign: 'center', marginTop: '20px', marginBottom: '16px' }}>
                <span style={{ fontSize: '32px' }}>{palette.icon}</span>
                <h3 style={{ color: '#fff', margin: '8px 0 4px' }}>Radar Alimentar</h3>
                <span style={{
                  display: 'inline-block',
                  fontSize: '11px',
                  letterSpacing: '0.5px',
                  fontWeight: 800,
                  background: palette.chip,
                  color: '#0b0b0b',
                  padding: '3px 10px',
                  borderRadius: '999px',
                }}>
                  {palette.label.toUpperCase()}
                </span>
              </div>

              <div style={{
                background: 'rgba(255,255,255,0.04)',
                border: `1px solid ${palette.chip}33`,
                borderLeft: `4px solid ${palette.chip}`,
                padding: '14px',
                borderRadius: '12px',
                marginBottom: '14px',
              }}>
                <h4 style={{ color: '#fff', margin: '0 0 8px', fontSize: '15px', lineHeight: '1.35' }}>
                  {renderTextSafe(bn.titulo || 'Atualização sobre este alimento')}
                </h4>
                {bn.fonte && (
                  <p style={{ color: '#cbd5e1', fontSize: '12px', margin: '0 0 6px' }}>
                    📚 Fonte: {renderTextSafe(bn.fonte)}
                  </p>
                )}
                {bn.data && (
                  <p style={{ color: '#94a3b8', fontSize: '11px', margin: 0 }}>
                    🗓️ {renderTextSafe(bn.data)}
                  </p>
                )}
                {bn.origem && (
                  <p style={{ color: '#64748b', fontSize: '10px', margin: '6px 0 0', fontStyle: 'italic' }}>
                    origem: {renderTextSafe(bn.origem)}{bn.tier ? ` · tier ${renderTextSafe(String(bn.tier))}` : ''}
                  </p>
                )}
              </div>

              {Array.isArray(bn.tags_matched) && bn.tags_matched.length > 0 && (
                <div style={{ marginBottom: '14px' }}>
                  <p style={{ color: '#94a3b8', fontSize: '11px', margin: '0 0 6px' }}>
                    Por que isso apareceu para você:
                  </p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {bn.tags_matched.map((tag, i) => (
                      <span
                        key={i}
                        style={{
                          fontSize: '10.5px',
                          background: 'rgba(255,255,255,0.07)',
                          color: '#e2e8f0',
                          padding: '3px 8px',
                          borderRadius: '999px',
                          border: '1px solid rgba(255,255,255,0.08)',
                        }}
                      >
                        {renderTextSafe(tag)}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {bn.url && (
                <a
                  href={bn.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="radar-alimentar-link"
                  style={{
                    display: 'block',
                    textAlign: 'center',
                    width: '100%',
                    padding: '10px 12px',
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.12)',
                    color: '#e2e8f0',
                    borderRadius: '10px',
                    fontSize: '13px',
                    textDecoration: 'none',
                    marginBottom: '10px',
                  }}
                >
                  Abrir matéria original ↗
                </a>
              )}

              <button
                onClick={() => setBreakingNewsModal(null)}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: palette.chip,
                  color: '#0b0b0b',
                  border: 'none',
                  borderRadius: '10px',
                  fontWeight: 800,
                  cursor: 'pointer',
                }}
              >
                Entendi
              </button>
            </div>
          </div>
        );
      })()}

      {/* MODAL DE CORREÇÃO MULTI */}
      {showMultiCorrection && (
        <div className="modal-overlay" onClick={() => setShowMultiCorrection(false)}>
          <div className="modal-content multi-correction" onClick={e => e.stopPropagation()}>
            {/* BOTÃO VOLTAR */}
            <button 
              className="modal-back-btn"
              onClick={() => setShowMultiCorrection(false)}
            >
              ← Voltar
            </button>
            
            <h3>✏️ Corrigir Identificação</h3>
            
            <div className="correction-form">
              <div className="form-group">
                <label>🍽️ Item Principal (ou primeiro item):</label>
                <input 
                  type="text"
                  placeholder="Ex: Maminha ao Molho, Peixe Grelhado..."
                  value={multiCorrections.principal}
                  onChange={e => setMultiCorrections({...multiCorrections, principal: e.target.value})}
                  autoFocus
                />
              </div>
              
              <div className="form-group">
                <label>🥗 Outros itens do prato (separados por vírgula):</label>
                <textarea 
                  placeholder="Ex: Arroz, Salada, Feijão, Camarão..."
                  value={multiCorrections.acompanhamentos}
                  onChange={e => setMultiCorrections({...multiCorrections, acompanhamentos: e.target.value})}
                  rows={4}
                />
              </div>
              
              <small className="help-text">
                💡 Edite apenas o que está errado. O cliente pode misturar proteínas e veganos no mesmo prato.
              </small>
            </div>
            
            <div className="modal-actions-fixed">
              <button 
                className="save-correction-btn" 
                onClick={saveMultiCorrection}
                disabled={creatingDish || !multiCorrections.principal.trim()}
              >
                {creatingDish ? '⏳ Salvando...' : '💾 Salvar Correção'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DE CORREÇÃO REMOVIDO - Agora a correção vai para a fila de moderação do admin */}

      {r && !r.ok && (
        <div className="err" data-testid="error-message">❌ {r.message}</div>
      )}

      {error && (
        <div className="err" data-testid="network-error">❌ Erro de conexão: {error}</div>
      )}

      {/* PREMIUM - Mini contador flutuante */}
      {premiumUser && dailySummary && !showPremium && (
        <div className="mini-counter" onClick={() => setShowPremium('dashboard')} data-testid="mini-counter">
          <div className="mini-value">{dailySummary.consumido?.toFixed(0) || 0}</div>
          <div className="mini-label">de {dailySummary.meta?.toFixed(0)} kcal</div>
          <div className="mini-progress">
            <div 
              className="mini-fill" 
              style={{ 
                width: `${Math.min(dailySummary.percentual || 0, 100)}%`,
                background: dailySummary.percentual >= 100 ? '#ef4444' : dailySummary.percentual >= 75 ? '#f59e0b' : '#22c55e'
              }} 
            />

      {/* PREMIUM - Botao perfil (sempre visivel) */}
      {premiumUser && !showPremium && !dailySummary && (
        <div 
          data-testid="premium-profile-btn"
          onClick={() => setShowPremium('dashboard')} 
          style={{
            position: 'fixed', bottom: '20px', right: '20px', zIndex: 1000,
            background: 'linear-gradient(135deg, #f59e0b, #d97706)', color: '#fff',
            width: '52px', height: '52px', borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '22px', cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(245,158,11,0.4)'
          }}
          title="Meu Perfil Premium"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
        </div>
      )}
          </div>
        </div>
      )}

      {/* PREMIUM - Botão flutuante */}
      <button 
        className={`premium-btn ${premiumUser ? 'logged-in' : ''}`}
        onClick={() => setShowPremium(premiumUser ? 'dashboard' : 'login')}
        data-testid="premium-button"
      >
        {premiumUser ? 'Dieta' : 'Premium'}
      </button>

      {/* PREMIUM - Modal */}
      {showPremium && (
        <div className="modal-overlay" onClick={() => setShowPremium(null)}>
          <div className="modal-content premium-modal" onClick={e => e.stopPropagation()}>
            {showPremium === 'login' && (
              <PremiumLogin 
                initialError={premiumBlockedMsg}
                onSuccess={(data) => {
                  if (data.premium_bloqueado) {
                    // Defesa belt-and-suspenders (PremiumLogin já deve ter bloqueado)
                    localStorage.removeItem('soulnutri_pin');
                    localStorage.removeItem('soulnutri_nome');
                    localStorage.removeItem('soulnutri_user');
                    setPremiumBlockedMsg(data.message || 'Acesso bloqueado.');
                    return;
                  }
                  setPremiumBlockedMsg('');
                  const user = { ...data.user, pin: localStorage.getItem('soulnutri_pin') };
                  setPremiumUser(user);
                  loadDailySummary();
                  // Se não tem dados físicos preenchidos, mostrar formulário
                  // Dados podem estar em user.peso ou user.perfil.peso
                  const temDadosFisicos = user.peso || (user.perfil && user.perfil.peso);
                  if (!temDadosFisicos) {
                    setShowPremium('profile');
                  } else {
                    setShowPremium('dashboard');
                  }
                }}
                onRegister={() => setShowPremium('register')}
                onCancel={() => setShowPremium(null)}
              />
            )}
            
            {showPremium === 'register' && (
              <PremiumRegister 
                onSuccess={(data) => {
                  const pin = localStorage.getItem('soulnutri_pin');
                  setPremiumUser({ nome: data.nome, pin, meta_calorica: data.meta_calorica });
                  setDailySummary({ nome: data.nome, meta: data.meta_calorica.meta_sugerida, consumido: 0, restante: data.meta_calorica.meta_sugerida, percentual: 0, pratos: [] });
                  // Ir direto para o dashboard - dados já foram coletados no registro
                  setShowPremium('dashboard');
                }}
                onCancel={() => setShowPremium('login')}
              />
            )}
            
            {/* Formulário de Perfil Premium */}
            {showPremium === 'profile' && premiumUser && (
              <PremiumProfileForm 
                user={premiumUser}
                onSave={(perfil) => {
                  setPremiumUser(prev => ({ ...prev, perfil }));
                  setShowPremium('dashboard');
                }}
                onSkip={() => setShowPremium('dashboard')}
              />
            )}
            
            {showPremium === 'dashboard' && premiumUser && (
              <DailyCounter 
                user={premiumUser}
                onLogout={handlePremiumLogout}
                onClose={() => setShowPremium(null)}
                onEditProfile={() => setShowPremium('edit-profile')}
              />
            )}
            
            {showPremium === 'edit-profile' && premiumUser && (
              <PremiumEditProfile 
                onSuccess={(data) => {
                  // Atualizar dados do usuário com nova meta calórica
                  setPremiumUser(prev => ({ ...prev, meta_calorica: data.meta_calorica }));
                  loadDailySummary();
                  setShowPremium('dashboard');
                }}
                onCancel={() => setShowPremium('dashboard')}
              />
            )}
          </div>
        </div>
      )}

      {/* Modal Check-in de Refeição (Premium) */}
      {showCheckin && premiumUser && (
        <div className="modal-overlay" onClick={() => setShowCheckin(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <CheckinRefeicao 
              pin={premiumUser.pin}
              nome={premiumUser.nome}
              onClose={() => setShowCheckin(false)}
              onSuccess={(data) => {
                // Atualizar o resumo diário após check-in
                loadDailySummary();
              }}
            />
          </div>
        </div>
      )}

      {/* Welcome Popup - Seleção de idioma para novos usuários */}
      {showWelcome && (
        <WelcomePopup onClose={handleWelcomeClose} />
      )}

      {/* Tutorial do Scanner Contínuo */}
      {showScannerTutorial && (
        <ScannerTutorial onClose={() => setShowScannerTutorial(false)} />
      )}

      {/* Nutri News Feed */}
      {showNutriFeed && (
        <NutritionFeed onClose={() => setShowNutriFeed(false)} />
      )}

      {/* Weekly Report & Achievements */}
      {showWeeklyReport && (
        <WeeklyReport 
          pin={localStorage.getItem('soulnutri_pin') || ''} 
          onClose={() => setShowWeeklyReport(false)} 
        />
      )}

      {/* Notification Panel */}
      <NotificationPanel
        userPin={premiumUser?.pin || ''}
        userName={premiumUser?.nome || ''}
        isVisible={showNotifications}
        onClose={() => setShowNotifications(false)}
      />

      {/* Rodapé */}
      <footer className="footer">
        <small>Powered by Emergent</small>
      </footer>
    </div>
  );
}

// Componente principal com Provider de internacionalização
function AppWithI18n() {
  return (
    <I18nProvider>
      <CameraErrorBoundary>
        <App />
      </CameraErrorBoundary>
    </I18nProvider>
  );
}

export default AppWithI18n;
